/*
 * The EaseFlow panel replica (sections/flow-panel.liquid): the plugin's own
 * panel, working in the browser next to a Program Monitor.
 *
 *   [data-flow-panel]   the stage: the panel ([data-fpx-app]) and the
 *                       monitor; its parts are [data-fpx="..."]
 *
 * The panel behaves as EaseFlow does in Premiere Pro (flow-premiere/js in the
 * plugin: curve-editor.js for the graph and the motion strip, main.js for
 * the library, menus, toasts and Apply, flow-core.js for the easing and
 * the keyframe planner), with the clip in the monitor standing in for
 * Premiere: Apply eases its Position, Scale and Rotation, and the
 * keyframes EaseFlow would write show under it.
 */
(() => {
  if (window.flowPanel) {
    window.flowPanel.init(document);
    return;
  }

  const SVG = 'http://www.w3.org/2000/svg';
  const calm = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // ---------------------------------------------------------------------
  // Curves (easeflow-core.js): {x1, y1, x2, y2} or {type: 'spring',
  // damping, frequency} - a damped spring over the whole move, damping
  // 4-16 (higher settles sooner), frequency 0-30 (higher rebounds more;
  // 0 arrives smoothly with no bounce). A curve can also run through
  // points ({x1, y1, x2, y2, points}): see the points below.

  const Y_MIN = -2;
  const Y_MAX = 3;
  const DAMPING_MIN = 4;
  const DAMPING_MAX = 16;
  const FREQUENCY_MAX = 30;
  const LINEAR = { x1: 0, y1: 0, x2: 1, y2: 1 };

  const clamp = (value, min, max) => (value < min ? min : value > max ? max : value);
  const finite = (value) => typeof value === 'number' && Number.isFinite(value);
  const mix = (a, b, amount) => a + (b - a) * amount;

  const makeSpring = (damping, frequency) => {
    const d = Number(damping);
    const w = Number(frequency);
    if (!finite(d) || !finite(w)) return null;
    return { type: 'spring', damping: clamp(d, DAMPING_MIN, DAMPING_MAX), frequency: clamp(w, 0, FREQUENCY_MAX) };
  };

  const isSpring = (curve) => !!curve && curve.type === 'spring';

  // Curve points: extra points the curve passes through between the two
  // keyframes, so one ease can speed up and slow down more than once. The
  // curve runs through them in cubic pieces; the two handles shape the
  // first and last piece. A point is [x, y, slope] - its handles a third
  // of the way to its neighbours in time - or, once its handles are shaped
  // apart, [x, y, slopeIn, slopeOut, reachIn, reachOut, corner].
  const POINT_MAX = 6;
  const POINT_GAP = 0.02;
  const SLOPE_MAX = 40;
  const REACH = 1 / 3;
  const REACH_MIN = 0.02;

  const pointHandles = (p) =>
    p.length === 3
      ? { slopeIn: p[2], slopeOut: p[2], reachIn: REACH, reachOut: REACH, corner: false }
      : { slopeIn: p[2], slopeOut: p[3], reachIn: p[4], reachOut: p[5], corner: !!p[6] };

  // A point from its spot and handles, short when it can be.
  const makePoint = (x, y, h) => {
    const slopeIn = clamp(h.slopeIn, -SLOPE_MAX, SLOPE_MAX);
    const slopeOut = h.corner ? clamp(h.slopeOut, -SLOPE_MAX, SLOPE_MAX) : slopeIn;
    const reachIn = clamp(h.reachIn, REACH_MIN, 1);
    const reachOut = clamp(h.reachOut, REACH_MIN, 1);
    if (!h.corner && Math.abs(reachIn - REACH) < 1e-9 && Math.abs(reachOut - REACH) < 1e-9) return [x, y, slopeIn];
    return [x, y, slopeIn, slopeOut, reachIn, reachOut, h.corner ? 1 : 0];
  };

  const normalizePoints = (list) => {
    if (!Array.isArray(list)) return null;
    const points = [];
    for (let i = 0; i < list.length; i += 1) {
      const p = list[i];
      if (!Array.isArray(p) || (p.length !== 3 && p.length !== 7) || !p.every(finite)) return null;
      points.push(makePoint(clamp(p[0], POINT_GAP, 1 - POINT_GAP), clamp(p[1], Y_MIN, Y_MAX), pointHandles(p)));
    }
    points.sort((a, b) => a[0] - b[0]);
    const kept = [];
    points.forEach((p) => {
      const last = kept.length ? kept[kept.length - 1][0] : 0;
      if (kept.length < POINT_MAX && p[0] - last >= POINT_GAP - 1e-9 && 1 - p[0] >= POINT_GAP - 1e-9) kept.push(p);
    });
    return kept;
  };

  const normalize = (curve) => {
    if (!curve) return null;
    if (Array.isArray(curve) ? curve[0] === 'spring' : curve.type === 'spring') {
      return Array.isArray(curve) ? makeSpring(curve[1], curve[2]) : makeSpring(curve.damping, curve.frequency);
    }
    const source = Array.isArray(curve) ? { x1: curve[0], y1: curve[1], x2: curve[2], y2: curve[3], points: curve[4] && curve[4].points } : curve;
    const values = [source.x1, source.y1, source.x2, source.y2].map(Number);
    if (!values.every(finite)) return null;
    const normalized = {
      x1: clamp(values[0], 0, 1),
      y1: clamp(values[1], Y_MIN, Y_MAX),
      x2: clamp(values[2], 0, 1),
      y2: clamp(values[3], Y_MIN, Y_MAX),
    };
    if (source.points !== undefined) {
      const points = normalizePoints(source.points);
      if (!points) return null;
      if (points.length) normalized.points = points;
    }
    return normalized;
  };

  const curvesEqual = (a, b, epsilon = 1e-6) => {
    if (isSpring(a) || isSpring(b)) {
      return isSpring(a) && isSpring(b) && Math.abs(a.damping - b.damping) <= epsilon && Math.abs(a.frequency - b.frequency) <= epsilon;
    }
    if (!a || !b) return false;
    const pa = a.points || [];
    const pb = b.points || [];
    if (pa.length !== pb.length) return false;
    for (let i = 0; i < pa.length; i += 1) {
      const ha = pointHandles(pa[i]);
      const hb = pointHandles(pb[i]);
      if (
        Math.abs(pa[i][0] - pb[i][0]) > epsilon ||
        Math.abs(pa[i][1] - pb[i][1]) > epsilon ||
        ha.corner !== hb.corner ||
        Math.abs(ha.slopeIn - hb.slopeIn) > epsilon ||
        Math.abs(ha.slopeOut - hb.slopeOut) > epsilon ||
        Math.abs(ha.reachIn - hb.reachIn) > epsilon ||
        Math.abs(ha.reachOut - hb.reachOut) > epsilon
      ) {
        return false;
      }
    }
    return (
      Math.abs(a.x1 - b.x1) <= epsilon &&
      Math.abs(a.y1 - b.y1) <= epsilon &&
      Math.abs(a.x2 - b.x2) <= epsilon &&
      Math.abs(a.y2 - b.y2) <= epsilon
    );
  };

  const isLinear = (curve) =>
    !isSpring(curve) && !curve.points && Math.abs(curve.x1 - curve.y1) < 1e-9 && Math.abs(curve.x2 - curve.y2) < 1e-9;

  const bezierAt = (t, p1, p2) => {
    const mt = 1 - t;
    return 3 * mt * mt * t * p1 + 3 * mt * t * t * p2 + t * t * t;
  };

  const bezierSlope = (t, p1, p2) => {
    const mt = 1 - t;
    return 3 * mt * mt * p1 + 6 * mt * t * (p2 - p1) + 3 * t * t * (1 - p2);
  };

  const solveT = (x, x1, x2) => {
    let t = x;
    for (let i = 0; i < 8; i += 1) {
      const error = bezierAt(t, x1, x2) - x;
      if (Math.abs(error) < 1e-10) return t;
      const slope = bezierSlope(t, x1, x2);
      if (Math.abs(slope) < 1e-7) break;
      t -= error / slope;
      if (t < 0 || t > 1) break;
    }
    let low = 0;
    let high = 1;
    t = x;
    for (let j = 0; j < 60; j += 1) {
      const value = bezierAt(t, x1, x2);
      if (Math.abs(value - x) < 1e-12) break;
      if (value < x) low = t;
      else high = t;
      t = (low + high) / 2;
    }
    return t;
  };

  // Springs: a damped oscillator from rest, with a smooth correction that
  // lands the second keyframe exactly without cutting the last of the
  // motion off.
  const springEase = (curve, x) => {
    const d = curve.damping;
    const w = curve.frequency;
    const response = (t) => 1 - Math.exp(-d * t) * (w === 0 ? 1 + d * t : Math.cos(w * t) + (d / w) * Math.sin(w * t));
    return response(x) + (1 - response(1)) * x * x * (3 - 2 * x);
  };

  // The cubic pieces of a curve with points, from (0, 0) to (1, 1):
  // [{x0, y0, cx1, cy1, cx2, cy2, x3, y3}]. A point's handles lie on its
  // slopes, reaching their share of the way to the next point in time;
  // the first and last piece take the curve's handles, scaled into the
  // box from one end to the next point.
  const curvePieces = (curve) => {
    const anchors = [[0, 0, 0]].concat(curve.points || [], [[1, 1, 0]]);
    const pieces = [];
    for (let i = 0; i < anchors.length - 1; i += 1) {
      const a = anchors[i];
      const b = anchors[i + 1];
      const dx = b[0] - a[0];
      const piece = { x0: a[0], y0: a[1], x3: b[0], y3: b[1] };
      if (i === 0) {
        piece.cx1 = a[0] + curve.x1 * dx;
        piece.cy1 = a[1] + curve.y1 * (b[1] - a[1]);
      } else {
        const out = pointHandles(a);
        piece.cx1 = a[0] + out.reachOut * dx;
        piece.cy1 = a[1] + out.slopeOut * out.reachOut * dx;
      }
      if (i === anchors.length - 2) {
        piece.cx2 = a[0] + curve.x2 * dx;
        piece.cy2 = a[1] + curve.y2 * (b[1] - a[1]);
      } else {
        const into = pointHandles(b);
        piece.cx2 = b[0] - into.reachIn * dx;
        piece.cy2 = b[1] - into.slopeIn * into.reachIn * dx;
      }
      pieces.push(piece);
    }
    return pieces;
  };

  const cubicAt = (t, p0, p1, p2, p3) => {
    const mt = 1 - t;
    return mt * mt * mt * p0 + 3 * mt * mt * t * p1 + 3 * mt * t * t * p2 + t * t * t * p3;
  };

  const pieceIndexAt = (pieces, x) => {
    const index = pieces.findIndex((piece) => x <= piece.x3);
    return index === -1 ? pieces.length - 1 : index;
  };

  const easePieces = (curve, x) => {
    const pieces = curvePieces(curve);
    const piece = pieces[pieceIndexAt(pieces, x)];
    const dx = piece.x3 - piece.x0;
    const t = solveT((x - piece.x0) / dx, (piece.cx1 - piece.x0) / dx, (piece.cx2 - piece.x0) / dx);
    return cubicAt(t, piece.y0, piece.cy1, piece.cy2, piece.y3);
  };

  const ease = (curve, x) => {
    if (x <= 0) return 0;
    if (x >= 1) return 1;
    if (isSpring(curve)) return springEase(curve, x);
    if (curve.points) return easePieces(curve, x);
    if (isLinear(curve)) return x;
    return bezierAt(solveT(x, curve.x1, curve.x2), curve.y1, curve.y2);
  };

  // How fast the curve goes at x (progress per unit of time).
  const curveSlope = (curve, x) => {
    const a = Math.max(0, x - 1e-4);
    const b = Math.min(1, x + 1e-4);
    return (ease(curve, b) - ease(curve, a)) / (b - a);
  };

  // The same curve, sampled: whether two curves draw the same move.
  const sameMove = (a, b) => {
    for (let j = 1; j < 32; j += 1) {
      if (!a || Math.abs(ease(a, j / 32) - ease(b, j / 32)) > 1e-4) return false;
    }
    return true;
  };

  // The curve with a point split into it at x, its shape kept (de
  // Casteljau): the new point smooth, its neighbours' handles shortened to
  // match. null where the handles can't say that exactly.
  const splitCurveAt = (curve, x) => {
    const pieces = curvePieces(curve);
    const index = pieceIndexAt(pieces, x);
    const s = pieces[index];
    const dx = s.x3 - s.x0;
    const t = solveT((x - s.x0) / dx, (s.cx1 - s.x0) / dx, (s.cx2 - s.x0) / dx);
    const lerp = (a, b) => a + (b - a) * t;
    const q0 = [lerp(s.x0, s.cx1), lerp(s.y0, s.cy1)];
    const q1 = [lerp(s.cx1, s.cx2), lerp(s.cy1, s.cy2)];
    const q2 = [lerp(s.cx2, s.x3), lerp(s.cy2, s.y3)];
    const r0 = [q0[0] + (q1[0] - q0[0]) * t, q0[1] + (q1[1] - q0[1]) * t];
    const r1 = [q1[0] + (q2[0] - q1[0]) * t, q1[1] + (q2[1] - q1[1]) * t];
    const m = [x, r0[1] + (r1[1] - r0[1]) * t];
    if (m[0] - r0[0] < 1e-9 || r1[0] - m[0] < 1e-9) return null;
    const next = { x1: curve.x1, y1: curve.y1, x2: curve.x2, y2: curve.y2 };
    const points = (curve.points || []).map((p) => p.slice());
    if (index === 0) {
      if (Math.abs(m[1]) < 1e-6) return null;
      next.x1 = q0[0] / m[0];
      next.y1 = q0[1] / m[1];
    } else {
      const a = points[index - 1];
      const ha = pointHandles(a);
      ha.reachOut = (q0[0] - a[0]) / (m[0] - a[0]);
      points[index - 1] = makePoint(a[0], a[1], ha);
    }
    if (index === pieces.length - 1) {
      if (Math.abs(1 - m[1]) < 1e-6) return null;
      next.x2 = (q2[0] - m[0]) / (1 - m[0]);
      next.y2 = (q2[1] - m[1]) / (1 - m[1]);
    } else {
      const b = points[index];
      const hb = pointHandles(b);
      hb.reachIn = (b[0] - q2[0]) / (b[0] - m[0]);
      points[index] = makePoint(b[0], b[1], hb);
    }
    const slope = (r1[1] - r0[1]) / (r1[0] - r0[0]);
    points.splice(
      index,
      0,
      makePoint(m[0], m[1], {
        slopeIn: slope,
        slopeOut: slope,
        reachIn: (m[0] - r0[0]) / (m[0] - s.x0),
        reachOut: (r1[0] - m[0]) / (s.x3 - m[0]),
        corner: false,
      })
    );
    next.points = points;
    const result = normalize(next);
    return sameMove(result, curve) ? result : null;
  };

  // The curve with a point added on it at x (null when there's no room).
  const addCurvePoint = (curve, x) => {
    if (isSpring(curve)) return null;
    const points = (curve.points || []).slice();
    if (points.length >= POINT_MAX || x < POINT_GAP || x > 1 - POINT_GAP) return null;
    if (points.some((p) => Math.abs(p[0] - x) < POINT_GAP)) return null;
    const split = splitCurveAt(curve, x);
    if (split) return split;
    points.push([x, ease(curve, x), clamp(curveSlope(curve, x), -SLOPE_MAX, SLOPE_MAX)]);
    return normalize({ x1: curve.x1, y1: curve.y1, x2: curve.x2, y2: curve.y2, points });
  };

  // The two pieces either side of a smooth point joined back into one,
  // when that reproduces them both (a point that only splits the curve).
  const joinPiecesAt = (curve, index) => {
    const h = pointHandles(curve.points[index]);
    if (Math.abs(h.slopeIn - h.slopeOut) > 1e-9) return null;
    const pieces = curvePieces(curve);
    const l = pieces[index];
    const r = pieces[index + 1];
    const into = l.x3 - l.cx2;
    const out = r.cx1 - r.x0;
    if (into < 1e-9 || out < 1e-9) return null;
    const t = into / (into + out);
    const c1 = [l.x0 + (l.cx1 - l.x0) / t, l.y0 + (l.cy1 - l.y0) / t];
    const c2 = [r.x3 + (r.cx2 - r.x3) / (1 - t), r.y3 + (r.cy2 - r.y3) / (1 - t)];
    const dx = r.x3 - l.x0;
    const next = { x1: curve.x1, y1: curve.y1, x2: curve.x2, y2: curve.y2 };
    const points = curve.points.map((p) => p.slice());
    if (index === 0) {
      if (Math.abs(r.y3 - l.y0) < 1e-6) return null;
      next.x1 = (c1[0] - l.x0) / dx;
      next.y1 = (c1[1] - l.y0) / (r.y3 - l.y0);
    } else {
      const a = points[index - 1];
      const ha = pointHandles(a);
      ha.reachOut = (c1[0] - a[0]) / dx;
      points[index - 1] = makePoint(a[0], a[1], ha);
    }
    if (index === points.length - 1) {
      if (Math.abs(r.y3 - l.y0) < 1e-6) return null;
      next.x2 = (c2[0] - l.x0) / dx;
      next.y2 = (c2[1] - l.y0) / (r.y3 - l.y0);
    } else {
      const b = points[index + 1];
      const hb = pointHandles(b);
      hb.reachIn = (b[0] - c2[0]) / dx;
      points[index + 1] = makePoint(b[0], b[1], hb);
    }
    points.splice(index, 1);
    if (points.length) next.points = points;
    const result = normalize(next);
    return sameMove(result, curve) ? result : null;
  };

  // The curve without point `index` - as it was before, when the point
  // only split it.
  const removeCurvePoint = (curve, index) => {
    const points = (curve.points || []).filter((p, i) => i !== index);
    const joined = curve.points && curve.points[index] ? joinPiecesAt(curve, index) : null;
    return joined || normalize({ x1: curve.x1, y1: curve.y1, x2: curve.x2, y2: curve.y2, points });
  };

  const curveExtent = (curve) => {
    let min = 0;
    let max = 1;
    if (isSpring(curve) || curve.points) {
      for (let s = 1; s < 400; s += 1) {
        const value = ease(curve, s / 400);
        min = Math.min(min, value);
        max = Math.max(max, value);
      }
      return { min, max };
    }
    for (let i = 1; i < 64; i += 1) {
      const y = bezierAt(i / 64, curve.y1, curve.y2);
      min = Math.min(min, y);
      max = Math.max(max, y);
    }
    return { min: Math.min(min, curve.y1, curve.y2), max: Math.max(max, curve.y1, curve.y2) };
  };

  // Out eases the first keyframe only, In the second; the other side
  // keeps what the pair had. A spring, or a curve with points, always
  // eases the whole move.
  const combineCurve = (mode, next, previous) => {
    if (isSpring(next)) return makeSpring(next.damping, next.frequency);
    if (next.points) return normalize(next);
    const prior = previous && !isSpring(previous) && !previous.points ? previous : LINEAR;
    if (mode === 'out') return { x1: next.x1, y1: next.y1, x2: prior.x2, y2: prior.y2 };
    if (mode === 'in') return { x1: prior.x1, y1: prior.y1, x2: next.x2, y2: next.y2 };
    return { x1: next.x1, y1: next.y1, x2: next.x2, y2: next.y2 };
  };

  const formatNumber = (value) => {
    const rounded = Math.round(value * 1000) / 1000;
    return rounded === 0 ? '0' : String(rounded);
  };

  // A curve with points: "x1, y1, x2, y2 | x, y, slope | ...".
  const formatCurve = (curve) =>
    isSpring(curve)
      ? `spring(${formatNumber(curve.damping)}, ${formatNumber(curve.frequency)})`
      : [curve.x1, curve.y1, curve.x2, curve.y2].map(formatNumber).join(', ') +
        (curve.points || []).map((p) => ` | ${p.map(formatNumber).join(', ')}`).join('');

  // What people paste: four numbers, cubic-bezier(), a cubic-bezier.com
  // link, a CSS keyword, spring(damping, frequency), or a curve with
  // points as the field shows one.
  const KEYWORD_CURVES = {
    linear: [0, 0, 1, 1],
    ease: [0.25, 0.1, 0.25, 1],
    'ease-in': [0.42, 0, 1, 1],
    'ease-out': [0, 0, 0.58, 1],
    'ease-in-out': [0.42, 0, 0.58, 1],
  };

  const parseSpring = (source) => {
    const match = /^spring\(\s*([\d.]+)\s*,\s*([\d.]+)\s*\)$/.exec(source);
    if (!match) return undefined;
    const d = Number(match[1]);
    const w = Number(match[2]);
    if (!finite(d) || !finite(w) || d < DAMPING_MIN || d > DAMPING_MAX || w > FREQUENCY_MAX) return null;
    return makeSpring(d, w);
  };

  const NUMBERS = /[-+]?(?:\d+\.?\d*|\.\d+)(?:e[-+]?\d+)?/g;

  const parsePointCurve = (source) => {
    const parts = source.split('|');
    const head = (parts[0].match(NUMBERS) || []).map(Number);
    if (head.length !== 4 || head[0] < 0 || head[0] > 1 || head[2] < 0 || head[2] > 1) return null;
    const points = parts.slice(1).map((part) => (part.match(NUMBERS) || []).map(Number));
    if (points.some((p) => (p.length !== 3 && p.length !== 7) || p[0] <= 0 || p[0] >= 1)) return null;
    const curve = normalize({ x1: head[0], y1: head[1], x2: head[2], y2: head[3], points });
    return curve && (curve.points || []).length === points.length ? curve : null;
  };

  const parseCurve = (text) => {
    let source = String(text || '').trim().toLowerCase();
    if (!source) return null;
    if (source.indexOf('|') !== -1) return parsePointCurve(source);
    const keyword = source.replace(/^.*timing-function\s*:\s*/, '').replace(/[;\s]+$/, '');
    if (KEYWORD_CURVES[keyword]) return normalize(KEYWORD_CURVES[keyword]);
    const hash = source.indexOf('#');
    if (hash !== -1 && source.indexOf('cubic-bezier.com') !== -1) {
      source = source.slice(hash + 1);
    } else if (source.indexOf('cubic-bezier') === -1) {
      const spring = parseSpring(source);
      if (spring !== undefined) return spring;
    }
    const matches = source.match(/[-+]?(?:\d+\.?\d*|\.\d+)(?:e[-+]?\d+)?/g);
    if (!matches || matches.length !== 4) return null;
    const values = matches.map(Number);
    if (values[0] < 0 || values[0] > 1 || values[2] < 0 || values[2] > 1) return null;
    return normalize(values);
  };

  // The keyframes EaseFlow writes for a 30-frame move (planSegment): Smart
  // keeps as few as hold every frame within 0.1% of the curve, Every
  // Frame one on each frame between the two keyframes.
  const FRAMES = 30;
  const TOLERANCE = 0.001;

  const planKeys = (curve, density) => {
    const samples = [];
    for (let i = 0; i <= FRAMES; i += 1) samples.push(ease(curve, i / FRAMES));
    const last = FRAMES;
    if (density === 'frame') return samples.slice(1, -1).map((_, index) => index + 1);
    const fits = (from, to) => {
      for (let i = from + 1; i < to; i += 1) {
        if (Math.abs(mix(samples[from], samples[to], (i - from) / (to - from)) - samples[i]) > TOLERANCE) return false;
      }
      return true;
    };
    const keys = [1];
    let from = 1;
    while (from < last - 1) {
      let reach = from + 1;
      for (let to = from + 2; to <= last - 1; to += 1) {
        if (fits(from, to)) reach = to;
        else break;
      }
      keys.push(reach);
      from = reach;
    }
    return keys;
  };

  // ---------------------------------------------------------------------
  // The built-in libraries (flow-core.js)

  const PENNER = [
    ['Linear', [0, 0, 1, 1]],
    ['Sine In', [0.12, 0, 0.39, 0]],
    ['Sine Out', [0.61, 1, 0.88, 1]],
    ['Sine In Out', [0.37, 0, 0.63, 1]],
    ['Quad In', [0.11, 0, 0.5, 0]],
    ['Quad Out', [0.5, 1, 0.89, 1]],
    ['Quad In Out', [0.45, 0, 0.55, 1]],
    ['Cubic In', [0.32, 0, 0.67, 0]],
    ['Cubic Out', [0.33, 1, 0.68, 1]],
    ['Cubic In Out', [0.65, 0, 0.35, 1]],
    ['Quart In', [0.5, 0, 0.75, 0]],
    ['Quart Out', [0.25, 1, 0.5, 1]],
    ['Quart In Out', [0.76, 0, 0.24, 1]],
    ['Quint In', [0.64, 0, 0.78, 0]],
    ['Quint Out', [0.22, 1, 0.36, 1]],
    ['Quint In Out', [0.83, 0, 0.17, 1]],
    ['Expo In', [0.7, 0, 0.84, 0]],
    ['Expo Out', [0.16, 1, 0.3, 1]],
    ['Expo In Out', [0.87, 0, 0.13, 1]],
    ['Circ In', [0.55, 0, 1, 0.45]],
    ['Circ Out', [0, 0.55, 0.45, 1]],
    ['Circ In Out', [0.85, 0, 0.15, 1]],
    ['Back In', [0.36, 0, 0.66, -0.56]],
    ['Back Out', [0.34, 1.56, 0.64, 1]],
    ['Back In Out', [0.68, -0.6, 0.32, 1.6]],
  ];

  // The Pro packs (Essentials, Apple's springs rebuilt from their bounce
  // and duration, and Multi-Point's curves with points) travel packed, like the plugin keeps them behind
  // its license: their numbers aren't for reading out of the page.
  const PRO_PACKS = (() => {
    const key = 'EaseFlow/adobebasics';
    const bytes = atob('PkMWR3w3NFVqABcWQiADEhZLTyh1T0BWalxDRwFXU0NTOD9NKEswHioOBw1kQDRHAVVIX05VTFNfWD4uaTpRIioFCxINTT9fTFdOUV9ZT0IYPF8+ZD8AEVtBNxsDFxZDXzJTXXNNQ0l2QlZbH09SMj9JOUMjHA0QLUNfPnZCX0IDUUpYTlVMUF9YPi5pOlEyLgUfVQM6VEFbSVJNQ0dSX3Q8LkkdTjwARgcQTU4+Uk9GXE9DaVFdVGpdMioDOkYuDBELAhoZAgcgQ18+dkJcTwNMVEFWSVJPR0VSLhhNKEcJGgoFXAkLABZHTjpDR1BfdU1DS3RZQ0YBUlEyP0k5QyAZERorBlFJHVxBRhdNVUFWSVJPR0VSLhg8X0cnTlUsdEM3Ag0KFglRRThRNhEBDCgLTVsYT1xaTlU/PF8yQSAoDhwRLkw+AkYCD01OPkASAxsKHSJDX1R0QlpAA1E5Mk4+QDIdCBMDPENfPmQfHwVGDwNNTlNMV0tFV110VS44ajdNJEEAFB8bRTMUGgoIUWk6URY2HgYZSENIXlJLVFlfX01FdzwuSR1OPAddCAoIQEk5QwAZERorBlFJcEJbTwNVSltWOD9NKEshHDAPEBxkQDRVXBEWBgwCQE1GR1ZfcE9FVBsxQywNIwsaDAYbQSIcChAuQ18+ZB8fBUYPA01OXUxZX1FNSnI8LkkdTioPWxMFTyAKFw8QEEFfHkMAFTQFARANTVBBVVROV11bWy4YTShHFRwdHkEGHU1OPkASAxsKHSJDX1BoXltbFk9UWD84TjpRPgwRJw0KR2o3TQRfEw0BBUdOVF1ZUF90UF1QdDEyW3RDLgoOCRtDXzJBADUTGgshTkNDAVZVQ1NRTFhLND5fHkMxCi8CCFUDOkYcEhcLDxRLT0drVUpJd1tBRBc8OTJORw9DSTI4UQMOBhdmPxsSX0NINFJLVE1DRVNdcU1CST1OHxhGDxAcQF85OkNHUkZpUV1Xc0BfKgM6VEFRSVJPQVxPQxhNKFVoWENHAVRIXz9JOVFdX09Da1RfVRtANEcBVkhfTFJXTUM0Tyh1T0tQalxBQBpNVDI/GD88XzJBIyQUAABmIQYTWAAdTU4+Uk9GRVNfdU9GSXdAFFVfDg0BFhZAWygyU11wTUNLc0BfKnIcOTJOPkAlHBwBHyBBJwQ2TkMsH09XQ1JJUk9HRVJfPkMDCi8CGwQNWz80UktTVF9ZTUZpUS5JHVxBQRpNVEFXSVI8LhQ+Lmk6UTYzHggSD0dELA0EERVRRThDa1JfVWpcQUMDUEgUQBUNCB0dEFF/OihVaF5aWx9PUVpOVUxSLkU4Q2tXX1VoWlpbH09XMj8YPzxfMkEgKQ4ESAsDTz9GFUZDOVVMVV9ZT0NrV19UahdNB0AIChsRR1g6KFlNQXBNQ0tyXkNFAVNIX0xVVE1DR1BAaVFdVnVAXioDOlRBVVBOUV1cUV91T0NTal5BRQNRSlxRSVJPQFpPQhg8DjgbQDRVYwAQCkI3FxIbS08odU9HSXZAX1kdTVVDGUcSDhoHFwBnWyg+dkJYWx9PVVpOVUxSLjQeLhhNKEcFHgoSX0FCTzELAxFRRThDa1JfVWpcQUYDUEgUQBUNCB0dEFF/OihVaFpDRwFQSF9MVVc8XzJTXXJZX1VoXVdbH09XMj8YPzxfMkEkLA8XRRMcTVt0UUpbTlVOUV1YVl90TQhHNgMGGVsSRlU5PlJPQFtPXnVPQlFqXDIqUjw5QzlHIAAQAkNVZSccFzIETVt0UUpcTlVOUV1dT0JpGlEVKQUBA1xDXjQ5VUxSX1lNRGlRLkkdXEFCGk1UQVZJUjwuFD4uaTpRKjAJHQVaD0ZDOVVMUF9ZTUVpUV1Tal1DDA0RCwYMERFDSTI4Q2tSRkl3Ql1bHzw5Ej84TjpROhQaKwZTJycPBFUDOlRBUElST0VFU11wTUJJPU4fGEYPEBxAXzk6Q0dWX3RPQFdqXDIqUjw5QzlHJw0SGhcaJkEgADIYAxINTT9fTFdOUV1fT0NrVF9UahdNB0AIChsRR1g6KFlNQH1NQkt3WkNHck0/X0xQWk1DR1pAaVEuSR1cQUAYTVVBUlZOUS40Hi4YTShHFAkNGFoPAE1OPlJPRUVTX3VPRkl3QBRVXw4NARYWQFsoMlNddlRfVGpfQUUDTFVBVElST0BaT0NrUkBJdzFDLB9PUkNSS1pNQzQ+Dhg8Xz5kLgACQQIBTy4EDAVRRThDa1RGSXZAX1kaTVVDGUcSDhoHFwBnWyg+dkJbQgNQSF1MU05MQkdXX3VPQFZqXEFEHE1VMk4+Uk9FW09Da1lFSXYxQywfT1NXTlROUF1dT151T0RJdkJcRANRSlxRSVM8XzJTXX1YX1VoVVlbHzw5Ej84Pxw=');
    let text = '';
    for (let i = 0; i < bytes.length; i += 1) text += String.fromCharCode(bytes.charCodeAt(i) ^ key.charCodeAt(i % key.length));
    const packs = JSON.parse(text);
    return { essentials: packs.e, apple: packs.a, multipoint: packs.m };
  })();
  const ESSENTIALS = PRO_PACKS.essentials;
  const APPLE = PRO_PACKS.apple;
  const MULTI_POINT = PRO_PACKS.multipoint;



  const library = (id, name, list, pro) => ({
    id,
    name,
    pro: !!pro,
    builtIn: true,
    presets: list.map((item) => ({ name: item[0], curve: normalize(item[1]) })),
  });

  // ---------------------------------------------------------------------
  // Little helpers

  const svgNode = (name, attributes, parent) => {
    const element = document.createElementNS(SVG, name);
    Object.keys(attributes || {}).forEach((key) => element.setAttribute(key, attributes[key]));
    if (parent) parent.appendChild(element);
    return element;
  };

  const element = (tag, className, parent) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (parent) parent.appendChild(node);
    return node;
  };

  const ICONS = {
    success: '<circle cx="8" cy="8" r="6.2" /><path d="M5.3 8.3l1.9 1.9 3.6-4" />',
    warning: '<path d="M8 2.2 14.3 13H1.7z" /><path d="M8 6.3v3.2M8 11.3v.1" />',
    error: '<circle cx="8" cy="8" r="6.2" /><path d="M8 4.8v3.8M8 11v.1" />',
    check: '<path d="M3.5 8.5l3 3 6-7" />',
  };

  const icon = (name, className) => `<svg class="${className}" viewBox="0 0 16 16" aria-hidden="true">${ICONS[name]}</svg>`;

  const listNames = (names, join = (list, last) => `${list} and ${last}`) =>
    names.length === 1 ? names[0] : join(names.slice(0, -1).join(', '), names[names.length - 1]);

  // The panel's words in the shop's language: the section's JSON
  // ([data-fpx-i18n]), with the English here as the fallback. [name]-style
  // tokens in a text are filled in from `values`.
  const readText = (root) => {
    let dict = {};
    const node = root.querySelector('[data-fpx-i18n]');
    if (node) {
      try {
        dict = JSON.parse(node.textContent) || {};
      } catch (error) {
        dict = {};
      }
    }
    return (key, english, values) => {
      const text = typeof dict[key] === 'string' && dict[key] ? dict[key] : english;
      return values ? text.replace(/\[(\w+)\]/g, (match, name) => (name in values ? String(values[name]) : match)) : text;
    };
  };

  // Pointer position from a mouse or touch event.
  const pointOf = (event) => ({ x: event.clientX, y: event.clientY });

  // ---------------------------------------------------------------------
  // The motion strip (curve-editor.js, MotionPreview)

  const LEAD_MS = 240;
  const FADE_IN_MS = 160;
  const REST_MS = 620;
  const FADE_OUT_MS = 220;
  const LOOPS = 3;
  const STRIP_INSET = 18;

  // How long the demo plays a move: a second, like the 30 frames it
  // stands for, but slower for a bouncy curve so its settle reads as
  // gentle instead of a quick shiver - the more it rebounds, the slower.
  // A curve with points gets a little longer for each one, so every
  // change of pace has time to show.
  const moveMsOf = (curves) => {
    const rebound = curves.reduce((most, curve) => (isSpring(curve) ? Math.max(most, curve.frequency / 18) : most), -1);
    const points = curves.reduce((most, curve) => (curve && curve.points ? Math.max(most, curve.points.length) : most), 0);
    const spring = rebound < 0 ? 1000 : Math.round(1350 + 700 * Math.min(1, rebound));
    return points ? Math.max(spring, 1150 + 130 * points) : spring;
  };
  const cycleMsOf = (move) => LEAD_MS + move + REST_MS + FADE_OUT_MS;

  const motionAt = (elapsed, move = 1000) => {
    const cycle = cycleMsOf(move);
    const e = Math.min(cycle, Math.max(0, elapsed));
    let opacity = 1;
    if (e < FADE_IN_MS) opacity = e / FADE_IN_MS;
    else if (e > cycle - FADE_OUT_MS) opacity = (cycle - e) / FADE_OUT_MS;
    return { t: Math.min(1, Math.max(0, (e - LEAD_MS) / move)), opacity };
  };

  const reachOf = (curves) => {
    let min = 0;
    let max = 1;
    curves.forEach((curve) => {
      for (let i = 1; curve && i < 48; i += 1) {
        const value = ease(curve, i / 48);
        min = Math.min(min, value);
        max = Math.max(max, value);
      }
    });
    return { min, max };
  };

  const createMotion = (host, initial) => {
    const [track, startMark, endMark] = [host.querySelector('.fpx-motion-track'), ...host.querySelectorAll('.fpx-motion-mark')];
    const ghostDot = host.querySelector('.fpx-motion-ghost');
    const dot = host.querySelector('.fpx-motion-dot');
    const state = {
      curve: Object.assign({}, initial),
      ghost: null,
      reach: reachOf([initial]),
      span: reachOf([initial]),
      width: 0,
      frame: null,
      lastFrame: 0,
      cycleStart: performance.now(),
      playUntil: 0,
      // Parked on the start until the panel comes into view.
      parked: true,
      hovering: false,
      visible: false,
      at: motionAt(LEAD_MS),
    };

    const draw = () => {
      const width = state.width;
      if (!width) return;
      const low = state.span.min;
      const inner = Math.max(1, width - 2 * STRIP_INSET);
      const scale = inner / Math.max(1e-6, state.span.max - low);
      const px = (value) => STRIP_INSET + (value - low) * scale;
      const place = (node, x) => {
        node.style.transform = `translate3d(${x.toFixed(2)}px, 0, 0)`;
      };
      const start = px(0);
      const end = px(1);
      place(track, start);
      track.style.width = `${Math.max(0, end - start).toFixed(2)}px`;
      place(startMark, start);
      place(endMark, end);
      place(dot, px(ease(state.curve, state.at.t)));
      dot.style.opacity = state.at.opacity.toFixed(3);
      const ghost = state.ghost && !curvesEqual(state.ghost, state.curve, 1e-4) ? state.ghost : null;
      ghostDot.style.display = ghost ? 'block' : 'none';
      if (ghost) {
        place(ghostDot, px(ease(ghost, state.at.t)));
        ghostDot.style.opacity = (state.at.opacity * 0.7).toFixed(3);
      }
    };

    const tick = (now) => {
      state.frame = null;
      const gap = Math.max(0, now - state.lastFrame);
      state.lastFrame = now;
      const glide = 1 - Math.exp(-Math.min(100, gap) / 90);
      state.span.min = Math.min(state.reach.min, state.span.min + (state.reach.min - state.span.min) * glide);
      state.span.max = Math.max(state.reach.max, state.span.max + (state.reach.max - state.span.max) * glide);
      const move = moveMsOf([state.curve]);
      const cycle = cycleMsOf(move);
      let elapsed = now - state.cycleStart;
      if (elapsed >= cycle) {
        const loops = Math.floor(elapsed / cycle);
        state.cycleStart += loops * cycle;
        elapsed -= loops * cycle;
      }
      const done = now >= state.playUntil && !state.hovering;
      if (done && ((elapsed >= FADE_IN_MS && elapsed < LEAD_MS) || gap > LEAD_MS - FADE_IN_MS)) {
        state.parked = true;
        state.span = Object.assign({}, state.reach);
        state.at = motionAt(LEAD_MS);
        draw();
        return;
      }
      state.at = motionAt(elapsed, move);
      draw();
      run();
    };

    const run = () => {
      if (state.frame === null && !document.hidden && state.visible) {
        if (!state.lastFrame || performance.now() - state.lastFrame > 100) state.lastFrame = performance.now();
        state.frame = requestAnimationFrame(tick);
      }
    };

    const halt = () => {
      if (state.frame !== null) {
        cancelAnimationFrame(state.frame);
        state.frame = null;
      }
    };

    const play = () => {
      const now = performance.now();
      state.playUntil = Math.max(state.playUntil, now + (calm() ? 1 : LOOPS) * cycleMsOf(moveMsOf([state.curve])) - (LEAD_MS - FADE_IN_MS));
      if (state.parked) {
        state.cycleStart = now - LEAD_MS;
        state.parked = false;
      }
      run();
    };

    const replay = () => {
      state.cycleStart = performance.now() - LEAD_MS;
      state.parked = false;
      play();
    };

    const measure = () => {
      state.width = host.clientWidth;
      draw();
    };

    host.addEventListener('mouseenter', () => {
      state.hovering = true;
      play();
    });
    host.addEventListener('mouseleave', () => {
      state.hovering = false;
    });
    host.addEventListener('click', replay);
    if (typeof ResizeObserver === 'function') new ResizeObserver(measure).observe(host);
    else window.addEventListener('resize', measure);
    measure();

    return {
      setCurve(curve, quiet) {
        if (curvesEqual(curve, state.curve, 1e-6)) return;
        state.curve = Object.assign({}, curve);
        state.reach = reachOf([state.curve, state.ghost]);
        if (!quiet) play();
        else if (state.parked) {
          state.span = Object.assign({}, state.reach);
          draw();
        }
      },
      setGhost(curve) {
        state.ghost = curve ? Object.assign({}, curve) : null;
        state.reach = reachOf([state.curve, state.ghost]);
        play();
      },
      setVisible(visible) {
        state.visible = visible;
        if (visible) {
          if (!state.parked) run();
        } else {
          state.hovering = false;
          halt();
        }
      },
      play,
      replay,
    };
  };

  // ---------------------------------------------------------------------
  // The graph (curve-editor.js, CurveEditor)

  const PAD_X = 16;
  const PAD_Y = 16;
  const KNOB_REACH = 22;
  // A spring's two handles, as in the plugin (curve-editor.js): Damping
  // slides left and right on a line from the first keyframe, Bounce
  // (the frequency) up and down on one from the second - both placed as
  // shares of the graph's height.
  const DAMPING_LEFT = 0.12;
  const DAMPING_SPAN = 0.38;
  const BOUNCE_X = 0.78;
  const GLIDE = normalize([0.2, 0.8, 0.2, 1]);
  // Curve points (curve-editor.js): a point's handle is drawn where it
  // really is, or - nearer than MIN_ARM px - that far out on its line, so
  // it can still be grabbed. A press within CURVE_REACH px of the curve
  // adds a point there; two clicks on a point within DOUBLE_CLICK_MS take
  // it out.
  const MIN_ARM = 16;
  const CURVE_REACH = 10;
  const DOUBLE_CLICK_MS = 400;

  // Where the two handles sit: as set, or - with points - scaled into the
  // first and last piece. A spring's are its Damping and Bounce.
  const handlesOf = (curve, range = { min: 0, max: 1 }) => {
    if (curve.points) {
      const pieces = curvePieces(curve);
      const last = pieces[pieces.length - 1];
      return { p1: { x: pieces[0].cx1, y: pieces[0].cy1 }, p2: { x: last.cx2, y: last.cy2 } };
    }
    if (!isSpring(curve)) return { p1: { x: curve.x1, y: curve.y1 }, p2: { x: curve.x2, y: curve.y2 } };
    const span = range.max - range.min;
    return {
      p1: { x: DAMPING_LEFT + (DAMPING_SPAN * (curve.damping - DAMPING_MIN)) / (DAMPING_MAX - DAMPING_MIN), y: range.min + span * 0.3 },
      p2: { x: BOUNCE_X, y: range.min + span * (0.1 + (0.8 * curve.frequency) / FREQUENCY_MAX) },
    };
  };

  const rangeFor = (curve, ghost) => {
    const extent = curveExtent(curve);
    const reach = ghost ? reachOf([ghost]) : { min: 0, max: 1 };
    const levels = isSpring(curve) ? [] : Object.values(handlesOf(curve)).map((handle) => handle.y);
    const low = Math.min(extent.min, reach.min, ...levels);
    const high = Math.max(extent.max, reach.max, ...levels);
    return { min: low < 0 ? low - 0.04 : 0, max: high > 1 ? high + 0.04 : 1 };
  };

  const easeOf = (curve, x) =>
    curve.blend ? mix(easeOf(curve.blend.from, x), easeOf(curve.blend.to, x), curve.blend.amount) : ease(curve, x);

  const createEditor = (svg, options) => {
    const host = svg.parentElement;
    const editor = {
      curve: normalize(options.curve),
      ghost: null,
      target: null,
      active: 'p1',
      dragging: null,
      width: 0,
      height: 0,
      gridKey: null,
      animation: null,
      morph: null,
      grab: { x: 0, y: 0 },
      springShown: null,
      // the target under the mouse; the last click on a point (for a
      // double-click); whether this press put a point in
      hovered: null,
      clicked: null,
      adding: false,
    };
    editor.range = rangeFor(editor.curve);
    const motion = options.motion ? createMotion(options.motion, editor.curve) : null;

    const gridLayer = svgNode('g', {}, svg);
    const square = svgNode('rect', { class: 'fpx-graph-square', rx: '3', ry: '3' }, svg);
    const ghostPath = svgNode('path', { class: 'fpx-ghost' }, svg);
    const lineOne = svgNode('line', { class: 'fpx-handle-line' }, svg);
    const lineTwo = svgNode('line', { class: 'fpx-handle-line' }, svg);
    const glow = svgNode('path', { class: 'fpx-curve-glow' }, svg);
    const path = svgNode('path', { class: 'fpx-curve' }, svg);
    const start = svgNode('circle', { class: 'fpx-endpoint', r: '3.5' }, svg);
    const end = svgNode('circle', { class: 'fpx-endpoint', r: '3.5' }, svg);
    const knobOne = svgNode('circle', { class: 'fpx-knob', r: '6.5' }, svg);
    const knobTwo = svgNode('circle', { class: 'fpx-knob', r: '6.5' }, svg);
    // The curve's points, and the handles of the selected one.
    const pointLayer = svgNode('g', { class: 'fpx-points' }, svg);
    const say = options.text || ((key, english) => english);
    // A spring's handles say what they do: their arrows and their names.
    const springTip = svgNode('g', { class: 'fpx-spring-tip' }, svg);
    const dampingArrows = svgNode('path', { class: 'fpx-spring-arrows' }, springTip);
    const dampingText = svgNode('text', { class: 'fpx-spring-text', 'text-anchor': 'middle' }, springTip);
    const bounceArrows = svgNode('path', { class: 'fpx-spring-arrows' }, springTip);
    const bounceText = svgNode('text', { class: 'fpx-spring-text', 'text-anchor': 'middle' }, springTip);
    dampingText.textContent = say('damping', 'Damping');
    bounceText.textContent = say('bounce', 'Bounce');

    const screenX = (x) => PAD_X + x * (editor.width - 2 * PAD_X);
    const screenY = (y) => PAD_Y + (1 - (y - editor.range.min) / (editor.range.max - editor.range.min)) * (editor.height - 2 * PAD_Y);
    const valueX = (px) => (px - PAD_X) / (editor.width - 2 * PAD_X);
    const valueY = (py, range) => {
      const view = range || editor.range;
      return view.min + (1 - (py - PAD_Y) / (editor.height - 2 * PAD_Y)) * (view.max - view.min);
    };

    const pathFor = (curve, from, amount) => {
      const p = (x, y) => `${screenX(x).toFixed(2)},${screenY(y).toFixed(2)}`;
      // Traced point by point, never as the bezier's own numbers.
      const count = Math.max(48, Math.round((editor.width - 2 * PAD_X) / 2));
      const parts = [`M${p(0, 0)}`];
      for (let i = 1; i <= count; i += 1) {
        const x = i / count;
        const y = from ? mix(easeOf(from, x), ease(curve, x), amount) : ease(curve, x);
        parts.push(`L${p(x, y)}`);
      }
      return parts.join(' ');
    };

    const renderGrid = () => {
      const key = `${editor.width}|${editor.height}|${editor.range.min}|${editor.range.max}`;
      if (key === editor.gridKey) return;
      editor.gridKey = key;
      while (gridLayer.firstChild) gridLayer.removeChild(gridLayer.firstChild);
      const left = screenX(0);
      const right = screenX(1);
      for (let i = 1; i < 4; i += 1) {
        const x = screenX(i / 4);
        svgNode('line', { x1: x, x2: x, y1: screenY(editor.range.max), y2: screenY(editor.range.min), class: 'fpx-grid-line' }, gridLayer);
        const y = screenY(i / 4);
        svgNode('line', { x1: left, x2: right, y1: y, y2: y, class: 'fpx-grid-line' }, gridLayer);
      }
      [0, 1].forEach((level) => {
        const y = screenY(level);
        svgNode('line', { x1: 4, x2: editor.width - 4, y1: y, y2: y, class: 'fpx-graph-limit' }, gridLayer);
      });
    };

    const render = () => {
      if (!editor.width) return;
      const curve = editor.curve;
      renderGrid();
      const x0 = screenX(0);
      const x1 = screenX(1);
      const yTop = screenY(1);
      const yBottom = screenY(0);
      square.setAttribute('x', x0);
      square.setAttribute('y', yTop);
      square.setAttribute('width', Math.max(0, x1 - x0));
      square.setAttribute('height', Math.max(0, yBottom - yTop));
      const d = editor.morph ? pathFor(curve, editor.morph.from, editor.morph.amount) : pathFor(curve);
      path.setAttribute('d', d);
      glow.setAttribute('d', d);
      if (editor.ghost && !curvesEqual(editor.ghost, curve, 1e-4)) {
        ghostPath.setAttribute('d', pathFor(editor.ghost));
        ghostPath.style.display = '';
      } else {
        ghostPath.style.display = 'none';
      }
      const place = (node, x, y) => {
        node.setAttribute('cx', screenX(x).toFixed(2));
        node.setAttribute('cy', screenY(y).toFixed(2));
      };
      const line = (node, ax, ay, bx, by) => {
        node.setAttribute('x1', screenX(ax).toFixed(2));
        node.setAttribute('y1', screenY(ay).toFixed(2));
        node.setAttribute('x2', screenX(bx).toFixed(2));
        node.setAttribute('y2', screenY(by).toFixed(2));
      };
      const handles = handlesOf(curve, editor.range);
      const spring = isSpring(curve);
      if (spring !== editor.springShown) {
        editor.springShown = spring;
        springTip.style.display = spring ? '' : 'none';
        svg.setAttribute(
          'aria-label',
          spring
            ? say('graph_spring', 'Spring. Drag Damping left or right, and Bounce up or down, or use the arrow keys.')
            : say('graph', 'Easing curve. Drag the handles, or use the arrow keys.')
        );
      }
      line(lineOne, 0, 0, handles.p1.x, handles.p1.y);
      place(knobOne, handles.p1.x, handles.p1.y);
      line(lineTwo, 1, 1, handles.p2.x, handles.p2.y);
      place(start, 0, 0);
      place(end, 1, 1);
      place(knobTwo, handles.p2.x, handles.p2.y);
      if (spring) {
        // Damping: arrows left and right, its name below
        const dx = screenX(handles.p1.x);
        const dy = screenY(handles.p1.y);
        dampingArrows.setAttribute('d', `M${dx - 16} ${dy - 4}l-4.5 4l4.5 4M${dx - 20.5} ${dy}h7M${dx + 16} ${dy - 4}l4.5 4l-4.5 4M${dx + 20.5} ${dy}h-7`);
        dampingText.setAttribute('x', dx.toFixed(2));
        dampingText.setAttribute('y', (dy + 24).toFixed(2));
        // Bounce: arrows up and down, its name below them
        const bx = screenX(handles.p2.x);
        const by = screenY(handles.p2.y);
        bounceArrows.setAttribute('d', `M${bx - 4} ${by - 16}l4 -4.5l4 4.5M${bx} ${by - 20.5}v7M${bx - 4} ${by + 16}l4 4.5l4 -4.5M${bx} ${by + 20.5}v-7`);
        bounceText.setAttribute('x', bx.toFixed(2));
        bounceText.setAttribute('y', (by + 38).toFixed(2));
      }
      knobOne.classList.toggle('fpx-active', editor.active === 'p1');
      knobTwo.classList.toggle('fpx-active', editor.active === 'p2');
      renderPoints();
      if (motion) motion.setCurve(curve, !editor.dragging);
    };

    // The point being edited (its index), -1 for none.
    const selectedPoint = () => {
      const match = /^[kt]:(\d+)/.exec(editor.active || '');
      const index = match ? Number(match[1]) : -1;
      return editor.curve.points && editor.curve.points[index] ? index : -1;
    };

    // Screen spots of point `index`'s incoming and outgoing handle, each
    // {x, y, short} - short when it's drawn further out than it is.
    const armSpots = (index) => {
      const pieces = curvePieces(editor.curve);
      const point = editor.curve.points[index];
      const handles = pointHandles(point);
      const cx = screenX(point[0]);
      const cy = screenY(point[1]);
      const across = screenX(1) - screenX(0);
      const up = screenY(1) - screenY(0);
      const ends = [
        { x: pieces[index].cx2, y: pieces[index].cy2 },
        { x: pieces[index + 1].cx1, y: pieces[index + 1].cy1 },
      ];
      return ends.map((end, side) => {
        const x = screenX(end.x);
        const y = screenY(end.y);
        if (Math.hypot(x - cx, y - cy) >= MIN_ARM) return { x, y, short: false };
        const sign = side ? 1 : -1;
        const dx = sign * across;
        const dy = sign * (side ? handles.slopeOut : handles.slopeIn) * up;
        const length = Math.hypot(dx, dy) || 1;
        return { x: cx + (dx / length) * MIN_ARM, y: cy + (dy / length) * MIN_ARM, short: true };
      });
    };

    // The points (a corner as a square), on the curve in its colour; the
    // selected one shows its two handles. Hidden while a new curve glides
    // in, then they fade in where it lands.
    function renderPoints() {
      while (pointLayer.firstChild) pointLayer.removeChild(pointLayer.firstChild);
      pointLayer.classList.toggle('fpx-hidden', !!editor.morph);
      const points = editor.curve.points || [];
      if (editor.morph || !points.length) return;
      const selected = selectedPoint();
      if (selected !== -1) {
        const arms = armSpots(selected);
        const cx = screenX(points[selected][0]).toFixed(2);
        const cy = screenY(points[selected][1]).toFixed(2);
        arms.forEach((arm) => {
          svgNode('line', { class: 'fpx-handle-line', x1: cx, y1: cy, x2: arm.x.toFixed(2), y2: arm.y.toFixed(2) }, pointLayer);
        });
        arms.forEach((arm, side) => {
          const id = `t:${selected}:${side ? 1 : -1}`;
          const knob = svgNode('circle', { class: 'fpx-arm', r: '4.5', cx: arm.x.toFixed(2), cy: arm.y.toFixed(2) }, pointLayer);
          knob.classList.toggle('fpx-hover', editor.hovered === id);
          knob.classList.toggle('fpx-active', editor.active === id);
        });
      }
      points.forEach((point, index) => {
        const id = `k:${index}`;
        const x = screenX(point[0]);
        const y = screenY(point[1]);
        const knob = pointHandles(point).corner
          ? svgNode('rect', { class: 'fpx-point fpx-corner', x: (x - 5).toFixed(2), y: (y - 5).toFixed(2), width: '10', height: '10', rx: '1.5' }, pointLayer)
          : svgNode('circle', { class: 'fpx-point', r: '5.5', cx: x.toFixed(2), cy: y.toFixed(2) }, pointLayer);
        knob.classList.toggle('fpx-hover', editor.hovered === id);
        knob.classList.toggle('fpx-active', selected === index);
      });
    }

    // Everything that can be grabbed, in screen px: [{id, x, y}].
    const targets = () => {
      const handles = handlesOf(editor.curve, editor.range);
      const list = [
        { id: 'p1', x: screenX(handles.p1.x), y: screenY(handles.p1.y) },
        { id: 'p2', x: screenX(handles.p2.x), y: screenY(handles.p2.y) },
      ];
      (editor.curve.points || []).forEach((point, index) => {
        list.push({ id: `k:${index}`, x: screenX(point[0]), y: screenY(point[1]) });
      });
      const selected = selectedPoint();
      if (selected !== -1) {
        const arms = armSpots(selected);
        list.push({ id: `t:${selected}:-1`, x: arms[0].x, y: arms[0].y }, { id: `t:${selected}:1`, x: arms[1].x, y: arms[1].y });
      }
      return list;
    };

    // How far (px) the pointer is from the curve, straight up or down.
    const curveDistance = (point) => {
      const x = valueX(point.x);
      if (x < 0 || x > 1) return Infinity;
      return Math.abs(screenY(ease(editor.curve, x)) - point.y);
    };

    const layout = () => {
      const box = host.getBoundingClientRect();
      editor.width = Math.max(80, Math.round(box.width));
      editor.height = Math.max(80, Math.round(box.height));
      svg.setAttribute('viewBox', `0 0 ${editor.width} ${editor.height}`);
      render();
    };

    const getCurve = () => Object.assign({}, editor.animation && editor.target ? editor.target : editor.curve);

    const stopAnimation = () => {
      if (editor.animation) {
        cancelAnimationFrame(editor.animation);
        editor.animation = null;
      }
      editor.morph = null;
    };

    const setCurve = (curve, settings = {}) => {
      const target = normalize(curve);
      if (!target) return;
      const shown = editor.morph
        ? { blend: { from: editor.morph.from, to: editor.curve, amount: editor.morph.amount } }
        : Object.assign({}, editor.curve);
      stopAnimation();
      editor.target = target;
      // A point that's gone takes its selection with it.
      const pointIndex = /^[kt]:(\d+)/.exec(editor.active || '');
      if (pointIndex && !(target.points && target.points[Number(pointIndex[1])])) editor.active = 'p1';
      if (!settings.animate || !editor.width || calm()) {
        editor.curve = target;
        editor.range = rangeFor(target, editor.ghost);
        render();
        return;
      }
      const from = shown;
      const fromRange = Object.assign({}, editor.range);
      const toRange = rangeFor(target, editor.ghost);
      const started = performance.now();
      const fromSpring = isSpring(from);
      const toSpring = isSpring(target);
      // The same curve (a drag let go) only lets its view settle.
      const same = !from.blend && curvesEqual(from, target, 1e-9);
      const step = (now) => {
        const progress = Math.min(1, (now - started) / 260);
        const eased = ease(GLIDE, progress);
        if (same) {
          editor.curve = target;
        } else if (fromSpring && toSpring) {
          editor.curve = { type: 'spring', damping: mix(from.damping, target.damping, eased), frequency: mix(from.frequency, target.frequency, eased) };
        } else if (fromSpring || toSpring || from.blend || from.points || target.points) {
          editor.curve = target;
          editor.morph = { from, amount: eased };
        } else {
          editor.curve = {
            x1: mix(from.x1, target.x1, eased),
            y1: mix(from.y1, target.y1, eased),
            x2: mix(from.x2, target.x2, eased),
            y2: mix(from.y2, target.y2, eased),
          };
        }
        editor.range = { min: mix(fromRange.min, toRange.min, eased), max: mix(fromRange.max, toRange.max, eased) };
        render();
        if (progress < 1) {
          editor.animation = requestAnimationFrame(step);
        } else {
          editor.animation = null;
          editor.morph = null;
          editor.curve = target;
          render();
        }
      };
      editor.animation = requestAnimationFrame(step);
    };

    const setGhost = (curve) => {
      const next = curve ? normalize(curve) : null;
      if ((next === null && editor.ghost === null) || (next && editor.ghost && curvesEqual(next, editor.ghost))) return;
      editor.ghost = next;
      if (!editor.dragging && !editor.animation) editor.range = rangeFor(editor.curve, editor.ghost);
      render();
      if (motion) motion.setGhost(next);
    };

    // Dragging. Pointer events here (the plugin uses mouse events, which
    // Premiere needs): on the web they cover touch and pens too.
    const pointFrom = (event) => {
      const box = svg.getBoundingClientRect();
      const point = pointOf(event);
      // The viewBox is the box's own size, so pixels map one to one.
      return { x: point.x - box.left, y: point.y - box.top };
    };

    // The nearest thing to grab ({handle, distance}); only: limit to these
    // ids (the two handles, for a press on empty graph). Points win a tie
    // with a handle sitting on top of them.
    const nearestHandle = (point, only) => {
      let best = { handle: 'p1', distance: Infinity };
      targets().forEach((target) => {
        if (only && only.indexOf(target.id) === -1) return;
        const distance = Math.hypot(target.x - point.x, target.y - point.y) - (target.id[0] === 'p' ? 0 : 0.5);
        if (distance < best.distance) best = { handle: target.id, distance: Math.max(0, distance) };
      });
      return best;
    };

    const setHover = (handle, overCurve) => {
      knobOne.classList.toggle('fpx-hover', handle === 'p1');
      knobTwo.classList.toggle('fpx-hover', handle === 'p2');
      svg.classList.toggle('fpx-over-knob', !!handle);
      svg.classList.toggle('fpx-over-curve', !handle && !!overCurve);
      if (editor.hovered !== handle) {
        editor.hovered = handle;
        if (editor.curve.points) renderPoints();
      }
    };

    // A change made with one gesture (adding or taking out a point).
    const commitChange = (curve) => {
      const before = getCurve();
      stopAnimation();
      editor.curve = curve;
      editor.target = curve;
      editor.range = rangeFor(curve, editor.ghost);
      render();
      if (options.onChange) options.onChange(getCurve());
      if (options.onCommit && !curvesEqual(before, curve, 1e-9)) options.onCommit(getCurve());
    };

    const addPointAt = (x) => {
      if (isSpring(editor.curve)) return false;
      const curve = addCurvePoint(getCurve(), x);
      if (!curve) return false;
      const index = curve.points.findIndex((point) => Math.abs(point[0] - x) < 1e-6);
      editor.active = `k:${index === -1 ? 0 : index}`;
      commitChange(curve);
      return true;
    };

    const removePoint = (index) => {
      if (!editor.curve.points || !editor.curve.points[index]) return;
      editor.active = 'p1';
      commitChange(removeCurvePoint(getCurve(), index));
    };

    // Drags a point (k:i) through time and value, or one end of its
    // tangent (t:i:side) to change how fast the curve goes there.
    const movePoint = (point, event) => {
      const match = /^([kt]):(\d+)/.exec(editor.dragging);
      const index = Number(match[2]);
      const points = editor.curve.points.map((p) => p.slice());
      const current = points[index];
      if (!current) return;
      const view = editor.dragRange || editor.range;
      let x = valueX(point.x);
      let y = valueY(point.y, view);
      if (match[1] === 'k') {
        // Shift: straight across or straight up and down from where the
        // drag began, whichever way the pointer has gone further.
        const from = editor.dragFrom && editor.dragFrom.points && editor.dragFrom.points[index];
        if (event.shiftKey && from) {
          const across = Math.abs(point.x - screenX(from[0]));
          const up = Math.abs(point.y - screenY(from[1]));
          if (across >= up) y = from[1];
          else x = from[0];
        }
        const before = index ? points[index - 1][0] : 0;
        const after = index < points.length - 1 ? points[index + 1][0] : 1;
        current[0] = Math.round(clamp(x, before + POINT_GAP, after - POINT_GAP) * 1000) / 1000;
        current[1] = Math.round(clamp(y, Y_MIN, Y_MAX) * 1000) / 1000;
      } else {
        // A handle: its slope from where the pointer is, its reach from how
        // far out in time. A smooth point's other handle swings in line;
        // Command/Ctrl makes a corner, Option/Alt lines them up again.
        const side = Number(/:(-?1)$/.exec(editor.dragging)[1]);
        const handles = pointHandles(current);
        const low = index ? points[index - 1][0] : 0;
        const high = index < points.length - 1 ? points[index + 1][0] : 1;
        const gap = side < 0 ? current[0] - low : high - current[0];
        const run = (x - current[0]) * side;
        const rise = (y - current[1]) * side;
        let slope = run < 1e-4 ? (rise >= 0 ? SLOPE_MAX : -SLOPE_MAX) : rise / run;
        if (event.shiftKey && Math.abs(slope) < 0.15) slope = 0;
        slope = Math.round(clamp(slope, -SLOPE_MAX, SLOPE_MAX) * 1000) / 1000;
        const arm = armSpots(index)[side < 0 ? 0 : 1];
        const out = Math.hypot(point.x - screenX(current[0]), point.y - screenY(current[1]));
        let reach = side < 0 ? handles.reachIn : handles.reachOut;
        if (!arm.short || out > MIN_ARM + 2) reach = Math.round(clamp(run / gap, REACH_MIN, 1) * 1000) / 1000;
        if (event.metaKey || event.ctrlKey) handles.corner = true;
        else if (event.altKey) handles.corner = false;
        if (side < 0) {
          handles.slopeIn = slope;
          handles.reachIn = reach;
          if (!handles.corner) handles.slopeOut = slope;
        } else {
          handles.slopeOut = slope;
          handles.reachOut = reach;
          if (!handles.corner) handles.slopeIn = slope;
        }
        points[index] = makePoint(current[0], current[1], handles);
      }
      editor.curve = normalize(Object.assign({}, editor.curve, { points }));
      const wanted = rangeFor(editor.curve, editor.ghost);
      editor.range = { min: Math.min(editor.range.min, wanted.min), max: Math.max(editor.range.max, wanted.max) };
      render();
      if (options.onChange) options.onChange(getCurve());
    };

    const moveHandle = (point, event) => {
      if (/^[kt]:/.test(editor.dragging)) {
        movePoint(point, event);
        return;
      }
      let x = Math.min(1, Math.max(0, valueX(point.x)));
      let y = Math.min(Y_MAX, Math.max(Y_MIN, valueY(point.y, editor.dragRange)));
      if (Math.abs(y - Math.round(y)) < 1e-9) y = Math.round(y);
      const curve = Object.assign({}, editor.curve);
      if (isSpring(curve)) {
        // Read in the view the drag started in, as the plugin does; Shift
        // snaps to the grid's steps.
        const view = editor.dragRange || editor.range;
        if (editor.dragging === 'p1') {
          const at = event.shiftKey ? Math.round(x * 10) / 10 : x;
          curve.damping = clamp(DAMPING_MIN + ((at - DAMPING_LEFT) / DAMPING_SPAN) * (DAMPING_MAX - DAMPING_MIN), DAMPING_MIN, DAMPING_MAX);
        } else {
          const raw = valueY(point.y, view);
          const at = event.shiftKey ? Math.round(raw * 10) / 10 : raw;
          const share = (at - view.min) / (view.max - view.min);
          curve.frequency = clamp(((share - 0.1) / 0.8) * FREQUENCY_MAX, 0, FREQUENCY_MAX);
        }
        const key = editor.dragging === 'p1' ? 'damping' : 'frequency';
        curve[key] = Math.round(curve[key] * 1000) / 1000;
      } else {
        if (event.shiftKey) {
          x = Math.round(x * 20) / 20;
          y = Math.round(y * 20) / 20;
        }
        if (curve.points) {
          // Back from where it is on the graph to the handle of the piece
          // it shapes, scaled to that piece's box.
          const pieces = curvePieces(curve);
          const piece = editor.dragging === 'p1' ? pieces[0] : pieces[pieces.length - 1];
          const up = piece.y3 - piece.y0;
          const hx = clamp((clamp(x, piece.x0, piece.x3) - piece.x0) / (piece.x3 - piece.x0), 0, 1);
          const hy = Math.abs(up) < 1e-6 ? null : Math.round(((y - piece.y0) / up) * 1000) / 1000;
          if (editor.dragging === 'p1') {
            curve.x1 = Math.round(hx * 1000) / 1000;
            if (hy !== null) curve.y1 = hy;
          } else {
            curve.x2 = Math.round(hx * 1000) / 1000;
            if (hy !== null) curve.y2 = hy;
          }
        } else if (editor.dragging === 'p1') {
          curve.x1 = x;
          curve.y1 = y;
          if (event.altKey) {
            curve.x2 = 1 - x;
            curve.y2 = 1 - y;
          }
        } else {
          curve.x2 = x;
          curve.y2 = y;
          if (event.altKey) {
            curve.x1 = 1 - x;
            curve.y1 = 1 - y;
          }
        }
      }
      editor.curve = normalize(curve);
      const wanted = rangeFor(editor.curve, editor.ghost);
      editor.range = { min: Math.min(editor.range.min, wanted.min), max: Math.max(editor.range.max, wanted.max) };
      render();
      if (options.onChange) options.onChange(getCurve());
    };

    const dragTo = (event) => {
      const point = pointFrom(event);
      moveHandle({ x: point.x + editor.grab.x, y: point.y + editor.grab.y }, event);
    };

    const endDrag = () => {
      if (!editor.dragging) return;
      editor.dragging = null;
      svg.classList.remove('fpx-dragging');
      setCurve(editor.curve, { animate: true });
      // A click that only selected something changed nothing; on a point,
      // it may be the first half of a double-click.
      const moved = !curvesEqual(editor.dragFrom, editor.curve, 1e-9);
      editor.clicked = !moved && !editor.adding && /^k:/.test(editor.active) ? { handle: editor.active, time: Date.now() } : null;
      if (options.onCommit && moved) options.onCommit(getCurve());
    };

    svg.addEventListener('pointerdown', (event) => {
      if ((event.pointerType === 'mouse' && event.button !== 0) || editor.dragging) return;
      event.preventDefault();
      if (options.onPress) options.onPress();
      if (editor.animation) {
        stopAnimation();
        editor.curve = editor.target;
        editor.range = rangeFor(editor.curve, editor.ghost);
        render();
      }
      const point = pointFrom(event);
      let nearest = nearestHandle(point);
      // Fingers are wider than a mouse pointer.
      let near = nearest.distance <= (event.pointerType === 'mouse' ? KNOB_REACH : KNOB_REACH * 1.6);
      const clicked = editor.clicked;
      editor.clicked = null;
      editor.adding = false;
      if (near && clicked && clicked.handle === nearest.handle && Date.now() - clicked.time < DOUBLE_CLICK_MS) {
        // The second click of a double-click on a point: out it goes.
        removePoint(Number(nearest.handle.slice(2)));
        svg.focus({ preventScroll: true });
        return;
      }
      if (!near && !isSpring(editor.curve) && curveDistance(point) <= CURVE_REACH) {
        // A press on the curve itself puts a point there, and the same
        // press drags it.
        svg.focus({ preventScroll: true });
        if (!addPointAt(Math.round(clamp(valueX(point.x), 0, 1) * 1000) / 1000)) return;
        nearest = { handle: editor.active, distance: 0 };
        near = true;
        editor.adding = true;
      }
      editor.dragFrom = normalize(editor.curve);
      // Grabbing empty graph grabs the closer handle and brings it to the
      // pointer; a knob grabbed off-centre keeps its offset.
      if (!near) nearest = nearestHandle(point, ['p1', 'p2']);
      const knob = targets().find((target) => target.id === nearest.handle);
      editor.grab = near && knob ? { x: knob.x - point.x, y: knob.y - point.y } : { x: 0, y: 0 };
      editor.dragging = nearest.handle;
      editor.dragRange = Object.assign({}, editor.range);
      editor.active = nearest.handle;
      svg.classList.add('fpx-dragging');
      try {
        svg.setPointerCapture(event.pointerId);
      } catch (error) {
        // capture is a nicety: moves still come while over the graph
      }
      svg.focus({ preventScroll: true });
      if (near) render();
      else dragTo(event);
    });

    svg.addEventListener('pointermove', (event) => {
      if (editor.dragging) {
        dragTo(event);
      } else if (event.pointerType === 'mouse') {
        const point = pointFrom(event);
        const nearest = nearestHandle(point);
        const near = nearest.distance <= KNOB_REACH;
        setHover(near ? nearest.handle : null, !near && !isSpring(editor.curve) && curveDistance(point) <= CURVE_REACH);
      }
    });
    svg.addEventListener('pointerup', endDrag);
    svg.addEventListener('pointercancel', endDrag);
    svg.addEventListener('lostpointercapture', endDrag);
    svg.addEventListener('pointerleave', () => setHover(null));

    svg.addEventListener('keydown', (event) => {
      const moves = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, 1], ArrowDown: [0, -1] };
      const selected = selectedPoint();
      if ((event.key === 'Delete' || event.key === 'Backspace') && selected !== -1) {
        event.preventDefault();
        removePoint(selected);
        return;
      }
      if (event.key === 'Tab') {
        // p1, then each point, then p2.
        const order = ['p1'].concat((editor.curve.points || []).map((p, i) => `k:${i}`), ['p2']);
        const at = order.indexOf(selected !== -1 ? `k:${selected}` : editor.active);
        const next = at + (event.shiftKey ? -1 : 1);
        if (at !== -1 && next >= 0 && next < order.length) {
          editor.active = order[next];
          render();
          event.preventDefault();
        }
        return;
      }
      const move = moves[event.key];
      if (!move) return;
      event.preventDefault();
      const amount = event.shiftKey ? 0.1 : 0.01;
      const before = getCurve();
      const curve = Object.assign({}, before);
      if (isSpring(before)) {
        // left and right: Damping; up and down: Bounce (the frequency)
        const step = event.shiftKey ? 1 : 0.1;
        if (move[0]) curve.damping = Math.round((curve.damping + move[0] * step) * 1000) / 1000;
        else curve.frequency = Math.round((curve.frequency + move[1] * step * 3) * 1000) / 1000;
      } else if (selected !== -1) {
        const points = before.points.map((p) => p.slice());
        const point = points[selected];
        if (/^t:/.test(editor.active)) {
          const handles = pointHandles(point);
          const key = /:-1$/.test(editor.active) ? 'slopeIn' : 'slopeOut';
          handles[key] = Math.round((handles[key] + (move[1] || move[0]) * amount * 10) * 1000) / 1000;
          if (!handles.corner) handles[key === 'slopeIn' ? 'slopeOut' : 'slopeIn'] = handles[key];
          points[selected] = makePoint(point[0], point[1], handles);
        } else {
          const low = selected ? points[selected - 1][0] + POINT_GAP : POINT_GAP;
          const high = selected < points.length - 1 ? points[selected + 1][0] - POINT_GAP : 1 - POINT_GAP;
          point[0] = Math.round(clamp(point[0] + move[0] * amount, low, high) * 1000) / 1000;
          point[1] = Math.round((point[1] + move[1] * amount) * 1000) / 1000;
        }
        curve.points = points;
      } else {
        const x = editor.active === 'p1' ? 'x1' : 'x2';
        const y = editor.active === 'p1' ? 'y1' : 'y2';
        curve[x] = Math.round((curve[x] + move[0] * amount) * 1000) / 1000;
        curve[y] = Math.round((curve[y] + move[1] * amount) * 1000) / 1000;
      }
      setCurve(curve);
      if (curvesEqual(before, getCurve(), 1e-9)) return;
      if (options.onChange) options.onChange(getCurve());
      if (!editor.keyCommit) editor.keyFrom = before;
      clearTimeout(editor.keyCommit);
      editor.keyCommit = setTimeout(() => {
        editor.keyCommit = null;
        if (options.onCommit && !curvesEqual(editor.keyFrom, getCurve(), 1e-9)) options.onCommit(getCurve());
      }, 400);
    });

    if (typeof ResizeObserver === 'function') new ResizeObserver(layout).observe(host);
    else window.addEventListener('resize', layout);
    layout();

    return {
      getCurve,
      setCurve,
      setGhost,
      preview() {
        if (motion) motion.replay();
      },
      setVisible(visible) {
        if (motion) motion.setVisible(visible);
      },
      startMotion() {
        if (motion) motion.play();
      },
    };
  };

  // ---------------------------------------------------------------------
  // The panel (main.js) and the monitor standing in for Premiere

  const PROPERTIES = [
    { id: 'position', name: 'Position' },
    { id: 'scale', name: 'Scale' },
    { id: 'rotation', name: 'Rotation' },
  ];

  // One loop of the clip in the monitor: a beat on the first keyframe,
  // the move (moveMsOf: a second, longer for bouncy curves), then a rest
  // on the second.
  const CLIP_LEAD_MS = 450;
  const CLIP_REST_MS = 1050;

  const setup = (root) => {
    if (root.dataset.fpxReady) return;
    root.dataset.fpxReady = 'true';
    const $ = (name) => root.querySelector(`[data-fpx="${name}"]`);
    const app = root.querySelector('[data-fpx-app]');
    const pid = root.dataset.pid || '';
    const tr = readText(root);
    const propName = (item) => tr(item.id, item.name);

    const settings = {
      mode: 'both',
      scope: 'playhead',
      density: 'smart',
      applyOnClick: true,
      applyEdits: false,
      autoEase: false,
      libraryId: 'multipoint',
      disabled: [],
    };

    const builtIn = [
      library('default', tr('library_default', 'Default'), PENNER),
      library('essentials', 'Essentials', ESSENTIALS, true),
      library('spring', 'Apple', APPLE, true),
      // A Pro pack the demo doesn't carry: its tab says what's in it.
      {
        id: 'creator',
        name: 'Creator Pro',
        pro: true,
        builtIn: true,
        locked: tr('creator_pro_text', '30 creator easings, speed ramps and multi-step moves included. Comes with EaseFlow Pro, along with every easing pack added later.'),
        presets: [],
      },
      library('multipoint', 'Multi-Point', MULTI_POINT, true),
    ];
    let userLibraries = [];
    const libraries = () => builtIn.concat(userLibraries);
    const currentLibrary = () => libraries().find((item) => item.id === settings.libraryId) || builtIn[0];

    // The clip's keyframes: per property, the curve EaseFlow eased its pair
    // with (null: the plain linear keyframes it started with) and the
    // frames EaseFlow put keyframes on.
    // The demo opens with the clip already eased with Multi-Point's
    // OPENING_NAME: a curve with points, which only EaseFlow can make.
    const OPENING_NAME = 'Elastic Settle';
    const OPENING = normalize(MULTI_POINT.find((item) => item[0] === OPENING_NAME)[1]);
    const openWith = () => ({ curve: OPENING, keys: planKeys(OPENING, settings.density) });
    let applied = { position: openWith(), scale: openWith(), rotation: openWith() };
    let appliedLabel = OPENING_NAME;
    const undoStack = [];
    const redoStack = [];

    const enabled = () => PROPERTIES.filter((item) => settings.disabled.indexOf(item.id) === -1);

    // -------------------------------------------------------------------
    // Toasts

    const toastLayer = $('toasts');
    let toastTimer = null;

    const toast = (text, tone, action) => {
      const previous = toastLayer.querySelector('.fpx-toast');
      if (previous) previous.remove();
      clearTimeout(toastTimer);
      if (!text) return;
      toastLayer.style.bottom = `${app.querySelector('.fpx-target-bar').offsetHeight + 8}px`;
      const node = element('div', `fpx-toast${tone === 'warning' || tone === 'error' ? ' fpx-warning' : ''}${action ? '' : ' fpx-no-action'}`);
      node.innerHTML = icon(tone === 'warning' || tone === 'error' ? tone : 'success', 'fpx-toast-icon');
      element('span', 'fpx-toast-text', node).textContent = text;
      const dismiss = () => {
        node.classList.add('fpx-leaving');
        setTimeout(() => node.remove(), 180);
      };
      if (action) {
        const button = element('button', 'fpx-toast-action', node);
        button.type = 'button';
        button.textContent = action.label;
        button.addEventListener('click', () => {
          dismiss();
          action.run();
        });
      }
      toastLayer.appendChild(node);
      toastTimer = setTimeout(dismiss, action ? 6000 : 3500);
      node.addEventListener('mouseenter', () => clearTimeout(toastTimer));
      node.addEventListener('mouseleave', () => {
        toastTimer = setTimeout(dismiss, 2500);
      });
    };

    // -------------------------------------------------------------------
    // Menus

    const menuLayer = $('menus');
    let openMenuState = null;

    const closeMenu = (refocus) => {
      if (!openMenuState) return;
      const { menu, anchor } = openMenuState;
      menu.remove();
      if (anchor) {
        anchor.classList.remove('fpx-open');
        anchor.setAttribute('aria-expanded', 'false');
        if (refocus) anchor.focus({ preventScroll: true });
      }
      openMenuState = null;
    };

    const setActiveItem = (menu, item) => {
      if (item) item.focus({ preventScroll: true });
      else menu.focus({ preventScroll: true });
    };

    // items: [{label, checked, disabled, hint, run}] or '-' or {heading}.
    const openMenu = (items, anchor, keyboard) => {
      closeMenu();
      const menu = element('div', 'fpx-menu', menuLayer);
      menu.tabIndex = -1;
      menu.setAttribute('role', 'menu');
      const hasChecks = items.some((item) => item && item.checked !== undefined);
      items.forEach((item) => {
        if (item === '-') {
          element('div', 'fpx-menu-separator', menu);
          return;
        }
        if (item.heading) {
          element('div', 'fpx-menu-heading', menu).textContent = item.heading;
          return;
        }
        const button = element('button', 'fpx-menu-item', menu);
        button.type = 'button';
        button.setAttribute('role', item.checked !== undefined ? 'menuitemcheckbox' : 'menuitem');
        if (item.checked !== undefined) button.setAttribute('aria-checked', item.checked ? 'true' : 'false');
        button.disabled = !!item.disabled;
        if (hasChecks) element('span', 'fpx-menu-check', button).innerHTML = item.checked ? icon('check', '') : '';
        element('span', 'fpx-menu-label', button).textContent = item.label;
        if (item.hint) element('span', 'fpx-menu-hint', button).textContent = item.hint;
        button.addEventListener('click', (event) => {
          event.stopPropagation();
          closeMenu(true);
          if (item.run) item.run(event);
        });
        button.addEventListener('mousemove', () => {
          if (!button.disabled && document.activeElement !== button) setActiveItem(menu, button);
        });
      });
      menu.addEventListener('mouseleave', () => setActiveItem(menu, null));
      menu.addEventListener('keydown', (event) => {
        const enabledItems = [...menu.querySelectorAll('.fpx-menu-item:not(:disabled)')];
        const index = enabledItems.indexOf(document.activeElement);
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          const step = event.key === 'ArrowDown' ? 1 : -1;
          const next = index === -1 ? (step > 0 ? 0 : enabledItems.length - 1) : (index + step + enabledItems.length) % enabledItems.length;
          setActiveItem(menu, enabledItems[next]);
        } else if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          closeMenu(true);
        } else if (event.key === 'Tab') {
          closeMenu(true);
        }
      });

      const box = app.getBoundingClientRect();
      const at = anchor.getBoundingClientRect();
      const width = menu.offsetWidth;
      const height = menu.offsetHeight;
      let left = at.right - box.left - width;
      let top = at.bottom - box.top + 4;
      if (top + height > box.height - 6) {
        top = at.top - box.top - height - 4;
        menu.classList.add('fpx-from-bottom');
      }
      left = Math.max(6, Math.min(box.width - width - 6, left));
      top = Math.max(6, Math.min(box.height - height - 6, top));
      menu.style.left = `${left}px`;
      menu.style.top = `${top}px`;
      anchor.classList.add('fpx-open');
      anchor.setAttribute('aria-expanded', 'true');
      openMenuState = { menu, anchor };
      setActiveItem(menu, keyboard ? menu.querySelector('.fpx-menu-item:not(:disabled)') : null);
    };

    const menuButton = (button, items) => {
      button.addEventListener('click', (event) => {
        if (openMenuState && openMenuState.anchor === button) {
          closeMenu();
          return;
        }
        // A click from the keyboard (Enter, Space) has no pointer position.
        openMenu(items(), button, event.detail === 0);
      });
    };

    // A press outside an open menu only closes it, as a Mac menu does.
    let swallowClick = false;
    document.addEventListener(
      'pointerdown',
      (event) => {
        swallowClick = false;
        if (openMenuState && !openMenuState.menu.contains(event.target) && !openMenuState.anchor.contains(event.target)) {
          closeMenu();
          swallowClick = app.contains(event.target);
          if (swallowClick && event.target.closest('.fpx-graph-svg')) {
            event.stopPropagation();
            event.preventDefault();
          }
        }
      },
      true
    );
    document.addEventListener(
      'click',
      (event) => {
        if (swallowClick) {
          swallowClick = false;
          event.stopPropagation();
          event.preventDefault();
        }
      },
      true
    );

    // -------------------------------------------------------------------
    // Sheets

    const sheet = ({ title, message, value, placeholder, confirm }) =>
      new Promise((resolve) => {
        closeMenu();
        const layer = element('div', 'fpx-sheet-layer', app);
        const form = element('form', 'fpx-sheet', layer);
        form.setAttribute('role', 'dialog');
        form.setAttribute('aria-modal', 'true');
        form.setAttribute('aria-label', title);
        element('h4', 'fpx-sheet-title', form).textContent = title;
        if (message) element('p', 'fpx-sheet-message', form).textContent = message;
        const input = element('input', 'fpx-sheet-input', form);
        input.type = 'text';
        input.value = value || '';
        input.placeholder = placeholder || '';
        input.setAttribute('aria-label', placeholder || title);
        input.maxLength = 60;
        const buttons = element('div', 'fpx-sheet-buttons', form);
        const cancel = element('button', 'fpx-secondary', buttons);
        cancel.type = 'button';
        cancel.textContent = tr('cancel', 'Cancel');
        const ok = element('button', 'fpx-primary', buttons);
        ok.type = 'submit';
        ok.textContent = confirm || 'OK';
        const finish = (result) => {
          layer.remove();
          resolve(result);
        };
        form.addEventListener('submit', (event) => {
          event.preventDefault();
          const name = input.value.trim();
          if (!name) {
            input.focus();
            return;
          }
          finish(name);
        });
        cancel.addEventListener('click', () => finish(null));
        layer.addEventListener('pointerdown', (event) => {
          if (event.target === layer) finish(null);
        });
        form.addEventListener('keydown', (event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            finish(null);
          }
        });
        input.focus({ preventScroll: true });
        input.select();
      });

    // -------------------------------------------------------------------
    // The editor

    const field = $('field');
    // A Pro preset's numbers stay out of sight (they're what Pro sells):
    // the field shows that it's a Pro curve instead, also while its handles
    // are dragged, until a curve is typed in or a Default one is picked.
    let masked = true;
    const shownValue = (curve) => (masked ? '' : formatCurve(curve));
    const showCurve = (curve) => {
      field.value = shownValue(curve);
      field.placeholder = masked ? tr('pro_curve', 'EaseFlow Pro curve') : '';
      field.classList.toggle('fpx-masked', masked);
    };
    const fieldEdited = () => field.value.trim() !== shownValue(editor.getCurve());
    const editor = createEditor($('graph'), {
      curve: OPENING,
      motion: $('motion'),
      text: tr,
      onPress: () => closeMenu(),
      onChange: (curve) => {
        showCurve(curve);
        field.classList.remove('fpx-invalid');
        markCurrentPreset(curve);
        renderMode();
      },
      onCommit: (curve) => {
        markCurrentPreset(curve);
        renderMode();
        if (settings.applyEdits) apply(curve);
      },
    });

    const setCurve = (curve, animate, pro = false) => {
      const normalized = normalize(curve);
      masked = pro;
      editor.setCurve(normalized, { animate });
      renderMode();
      showCurve(normalized);
      field.classList.remove('fpx-invalid');
      markCurrentPreset(normalized);
      if (animate) setTimeout(() => editor.preview(), 280);
    };

    const commitField = () => {
      const parsed = parseCurve(field.value);
      if (!parsed) {
        field.classList.remove('fpx-invalid');
        void field.offsetWidth; // restart the shake
        field.classList.add('fpx-invalid');
        return false;
      }
      const changed = !curvesEqual(parsed, editor.getCurve(), 1e-9);
      setCurve(parsed, true);
      if (changed && settings.applyEdits) apply(parsed);
      return true;
    };

    field.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        commitField();
      } else if (event.key === 'Escape') {
        showCurve(editor.getCurve());
        field.classList.remove('fpx-invalid');
        field.blur();
      }
    });
    field.addEventListener('blur', () => {
      if (fieldEdited()) commitField();
    });
    field.addEventListener('focus', () => field.select());

    // -------------------------------------------------------------------
    // The library

    const tabsHost = $('tabs');
    const grid = $('grid');

    const thumbnail = (curve) => {
      const extent = curveExtent(curve);
      const range = { min: Math.min(0, extent.min) - 0.08, max: Math.max(1, extent.max) + 0.08 };
      const width = 60;
      const height = 40;
      const pad = 7;
      const px = (x) => (pad + x * (width - 2 * pad)).toFixed(2);
      const y = (value) => (pad + (1 - (value - range.min) / (range.max - range.min)) * (height - 2 * pad)).toFixed(2);
      // Traced, so a preset's numbers aren't in the picture's markup.
      const parts = [`M${px(0)},${y(0)}`];
      for (let i = 1; i <= 96; i += 1) parts.push(`L${px(i / 96)},${y(ease(curve, i / 96))}`);
      const d = parts.join(' ');
      return (
        `<svg viewBox="0 0 ${width} ${height}" preserveAspectRatio="none" aria-hidden="true">` +
        `<line class="fpx-thumb-base" x1="${pad}" x2="${width - pad}" y1="${y(0)}" y2="${y(0)}" />` +
        `<line class="fpx-thumb-base" x1="${pad}" x2="${width - pad}" y1="${y(1)}" y2="${y(1)}" />` +
        `<path class="fpx-thumb-curve" vector-effect="non-scaling-stroke" d="${d}" />` +
        `</svg>`
      );
    };

    // The tab row fades out at an edge with more tabs past it.
    const fadeTabs = () => {
      const max = tabsHost.scrollWidth - tabsHost.clientWidth;
      tabsHost.classList.toggle('fpx-fade-start', tabsHost.scrollLeft > 1);
      tabsHost.classList.toggle('fpx-fade-end', tabsHost.scrollLeft < max - 1);
    };
    tabsHost.addEventListener('scroll', fadeTabs, { passive: true });

    // The selected tab, scrolled into the tab row (not the page) clear of
    // the fades, when the row is too narrow for them all.
    const revealTab = () => {
      const tab = tabsHost.querySelector('[aria-selected="true"]');
      if (tab) {
        const left = tab.getBoundingClientRect().left - tabsHost.getBoundingClientRect().left + tabsHost.scrollLeft;
        if (left < tabsHost.scrollLeft + 22) tabsHost.scrollLeft = left - 22;
        else if (left + tab.offsetWidth > tabsHost.scrollLeft + tabsHost.clientWidth - 28) tabsHost.scrollLeft = left + tab.offsetWidth - tabsHost.clientWidth + 28;
      }
      fadeTabs();
    };

    const renderTabs = () => {
      // rebuilt in place: the row keeps where it was scrolled to
      const scrolled = tabsHost.scrollLeft;
      tabsHost.innerHTML = '';
      libraries().forEach((item) => {
        const tab = element('button', 'fpx-library-tab', tabsHost);
        tab.type = 'button';
        tab.setAttribute('role', 'tab');
        const selected = item.id === currentLibrary().id;
        tab.setAttribute('aria-selected', selected ? 'true' : 'false');
        tab.tabIndex = selected ? 0 : -1;
        tab.title = item.pro ? tr('pro_tab_title', '[name] - in EaseFlow Pro', { name: item.name }) : item.name;
        element('span', '', tab).textContent = item.name;
        if (item.pro) element('span', 'fpx-pro', tab).textContent = 'PRO';
        tab.addEventListener('click', () => {
          settings.libraryId = item.id;
          renderLibrary();
        });
      });
      tabsHost.scrollLeft = scrolled;
      fadeTabs();
    };

    tabsHost.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      const list = libraries();
      const index = list.indexOf(currentLibrary());
      const next = list[(index + (event.key === 'ArrowRight' ? 1 : -1) + list.length) % list.length];
      settings.libraryId = next.id;
      renderLibrary();
      const tab = tabsHost.querySelector('[aria-selected="true"]');
      if (tab) tab.focus();
    });

    const renderLibrary = () => {
      renderTabs();
      revealTab();
      grid.innerHTML = '';
      const current = currentLibrary();
      if (current.locked) {
        const card = element('div', 'fpx-locked', grid);
        const title = element('p', 'fpx-locked-title', card);
        element('span', '', title).textContent = current.name;
        element('span', 'fpx-pro', title).textContent = 'PRO';
        element('p', 'fpx-locked-text', card).textContent = current.locked;
        const get = element('a', 'fpx-primary', card);
        get.href = root.dataset.proUrl || '#';
        get.dataset.flowChoose = 'Pro';
        get.textContent = tr('get_pro', 'Get EaseFlow Pro');
        // flow-page.js picks Pro in the buy box instead of following the link
        if (window.flowPage) window.flowPage.init(card);
      } else if (!current.presets.length) {
        const empty = element('div', 'fpx-preset-name', grid);
        empty.style.gridColumn = '1 / -1';
        empty.style.padding = '18px 8px';
        empty.style.whiteSpace = 'normal';
        empty.textContent = tr('empty_library', 'Save a curve with + to keep it here.');
      }
      current.presets.forEach((preset) => {
        const tile = element('button', 'fpx-preset', grid);
        tile.type = 'button';
        tile.setAttribute('role', 'option');
        const pro = !!(current.pro || preset.pro);
        tile.title = pro ? preset.name : `${preset.name}\n${formatCurve(preset.curve)}`;
        tile.innerHTML = `<span class="fpx-thumb">${thumbnail(preset.curve)}</span>`;
        element('span', 'fpx-preset-name', tile).textContent = preset.name;
        tile.fpxPreset = preset;
        tile.addEventListener('click', (event) => {
          setCurve(preset.curve, true, pro);
          // Option/Alt-click only loads it; so does a click with nothing to ease.
          if (settings.applyOnClick && !event.altKey && enabled().length) apply(preset.curve, preset.name);
        });
      });
      grid.scrollTop = 0;
      markCurrentPreset();
    };

    function markCurrentPreset(curve) {
      const current = curve || editor.getCurve();
      grid.querySelectorAll('.fpx-preset').forEach((tile) => {
        const on = curvesEqual(tile.fpxPreset.curve, current, 5e-4);
        tile.classList.toggle('fpx-current', on);
        tile.setAttribute('aria-selected', on ? 'true' : 'false');
      });
    }

    const presetNameFor = (curve) => {
      const entry = currentLibrary().presets.find((preset) => curvesEqual(preset.curve, curve, 5e-4));
      return entry ? entry.name : null;
    };

    $('save').addEventListener('click', async () => {
      const curve = editor.getCurve();
      const own = userLibraries.find((item) => item.id === settings.libraryId) || userLibraries[0];
      const target = own || { id: 'my-curves', name: tr('my_curves', 'My Curves'), presets: [] };
      const count = target.presets.length + 1;
      const name = await sheet({
        title: tr('save_curve', 'Save Curve'),
        message: masked ? tr('save_message', 'Adds this curve to your library.') : tr('save_message_curve', 'Adds [curve] to your library.', { curve: formatCurve(curve) }),
        value: tr('custom_name', 'Custom [count]', { count }),
        placeholder: tr('name', 'Name'),
        confirm: tr('save', 'Save'),
      });
      if (!name) return;
      if (!own) userLibraries = userLibraries.concat([target]);
      const names = target.presets.map((preset) => preset.name);
      let unique = name.slice(0, 60);
      for (let n = 2; names.indexOf(unique) !== -1; n += 1) unique = `${name.slice(0, 56)} ${n}`;
      target.presets.push({ name: unique, curve: Object.assign({}, curve), pro: masked });
      settings.libraryId = target.id;
      renderLibrary();
      const last = grid.lastElementChild;
      if (last) last.scrollIntoView({ block: 'nearest' });
      toast(tr('saved_to', 'Saved “[name]” to [library]', { name: unique, library: target.name }));
    });

    const insidePremiere = (text) => toast(text);

    const libraryMenu = () => {
      const current = currentLibrary();
      const own = !current.builtIn;
      return [
        {
          label: tr('new_library_item', 'New Library…'),
          run: async () => {
            const name = await sheet({ title: tr('new_library', 'New Library'), placeholder: tr('name', 'Name'), confirm: tr('create', 'Create') });
            if (!name) return;
            const id = `lib-${Date.now()}`;
            userLibraries = userLibraries.concat([{ id, name: name.slice(0, 60), presets: [] }]);
            settings.libraryId = id;
            renderLibrary();
          },
        },
        { label: tr('import_library', 'Import Library…'), run: () => insidePremiere(tr('import_note', 'Importing a library works in EaseFlow inside Premiere Pro.')) },
        {
          label: tr('export_library', 'Export “[library]”…', { library: current.name }),
          disabled: !current.presets.length,
          run: () => insidePremiere(tr('export_note', 'Exporting a library works in EaseFlow inside Premiere Pro.')),
        },
        '-',
        {
          label: tr('delete_library', 'Delete “[library]”…', { library: current.name }),
          disabled: !own,
          run: () => {
            userLibraries = userLibraries.filter((item) => item !== current);
            settings.libraryId = 'essentials';
            renderLibrary();
            toast(tr('deleted', 'Deleted “[library]”', { library: current.name }));
          },
        },
      ];
    };
    menuButton($('library-menu'), libraryMenu);

    // -------------------------------------------------------------------
    // The target bar: properties, Out / In & Out / In, scope

    const chips = [...root.querySelectorAll('[data-fpx-chip]')];
    chips.forEach((chip) => {
      chip.addEventListener('click', () => {
        const id = chip.dataset.fpxChip;
        const off = settings.disabled.indexOf(id) === -1;
        settings.disabled = off ? settings.disabled.concat([id]) : settings.disabled.filter((item) => item !== id);
        // Switched back on, it joins the easing the others have, so the
        // clip shows it straight away.
        const shared = enabled().find((item) => item.id !== id && applied[item.id]);
        if (!off && shared && (!applied[id] || !curvesEqual(applied[id].curve, applied[shared.id].curve))) {
          applied[id] = { curve: Object.assign({}, applied[shared.id].curve), keys: applied[shared.id].keys.slice() };
        }
        renderTargets();
        monitor.render();
        monitor.replay();
      });
    });

    const modeControl = $('mode');
    const modeButtons = [...modeControl.querySelectorAll('[data-mode]')];
    modeButtons.forEach((button) => {
      button.dataset.title = button.title;
      button.addEventListener('click', () => {
        settings.mode = button.dataset.mode;
        renderMode();
      });
    });

    // A spring, or a curve with points, always eases the whole move.
    function renderMode() {
      const curve = editor.getCurve();
      const spring = isSpring(curve);
      const whole = spring || !!curve.points;
      modeButtons.forEach((button) => {
        const selected = button.dataset.mode === settings.mode;
        button.setAttribute('aria-checked', selected ? 'true' : 'false');
        button.tabIndex = selected ? 0 : -1;
        button.title = spring
          ? tr('spring_title', 'A spring always eases the whole move, from one keyframe to the next')
          : whole
            ? tr('points_title', 'A curve with points always eases the whole move, from one keyframe to the next')
            : button.dataset.title;
      });
      modeControl.classList.toggle('fpx-whole', whole);
    }

    modeControl.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      const index = modeButtons.findIndex((button) => button.dataset.mode === settings.mode);
      const next = modeButtons[(index + (event.key === 'ArrowRight' ? 1 : -1) + modeButtons.length) % modeButtons.length];
      settings.mode = next.dataset.mode;
      renderMode();
      next.focus();
    });

    const scopeButton = $('scope');
    menuButton(scopeButton, () => {
      const choose = (scope) => () => {
        settings.scope = scope;
        renderTargets();
      };
      return [
        { label: tr('segment_at_playhead', 'Segment at Playhead'), checked: settings.scope === 'playhead', run: choose('playhead') },
        { label: tr('all_keyframes', 'All Keyframes'), checked: settings.scope === 'all', run: choose('all') },
      ];
    });

    const status = $('status');

    function renderTargets() {
      chips.forEach((chip) => {
        const property = PROPERTIES.find((item) => item.id === chip.dataset.fpxChip);
        const off = settings.disabled.indexOf(property.id) !== -1;
        chip.classList.toggle('fpx-off', off);
        chip.setAttribute('aria-pressed', off ? 'false' : 'true');
        chip.title = off
          ? tr('chip_off', '[property] is off - click to ease it too', { property: propName(property) })
          : tr('chip_on', '[property] will be eased - click to leave it out', { property: propName(property) });
      });
      $('scope-label').textContent = settings.scope === 'all' ? tr('all_keyframes', 'All Keyframes') : tr('playhead', 'Playhead');

      const ready = enabled();
      // Auto Mode's switch beside Apply: blue while on.
      const auto = $('auto');
      auto.classList.toggle('fpx-on', !!settings.autoEase);
      auto.setAttribute('aria-pressed', String(!!settings.autoEase));
      auto.title = settings.autoEase
        ? tr('auto_on_title', 'Auto Mode is on: easing applies when you create the second keyframe - click to turn off')
        : tr('auto_off_title', 'Auto Mode is off - click to ease new keyframes automatically');
      // Under the curve: only what's in the way, as in the plugin.
      status.textContent = ready.length ? '' : tr('turn_on_property', 'Turn on a property below.');
      status.hidden = !!ready.length;

      // The easing already on the pair, as the graph's dashed ghost curve.
      const first = ready.find((item) => applied[item.id]);
      editor.setGhost(first ? applied[first.id].curve : null);
      monitor.render();
    }

    // -------------------------------------------------------------------
    // Apply, Undo, Redo, Read, Remove

    const snapshot = () => ({ applied: Object.assign({}, applied), label: appliedLabel });
    const restore = (state) => {
      applied = Object.assign({}, state.applied);
      appliedLabel = state.label;
    };

    const record = (label, before) => {
      undoStack.push({ label, before, after: snapshot() });
      if (undoStack.length > 50) undoStack.shift();
      redoStack.length = 0;
    };

    const keyCount = () => Object.values(applied).reduce((sum, item) => sum + (item ? item.keys.length : 0), 0);

    function apply(curve, presetName, auto) {
      const names = enabled();
      if (!names.length) {
        toast(tr('turn_on_property', 'Turn on a property below.'), 'warning');
        return;
      }
      const easing = normalize(curve || editor.getCurve());
      const label = presetName || presetNameFor(easing);
      const before = snapshot();
      names.forEach((item) => {
        const previous = applied[item.id] ? applied[item.id].curve : null;
        const next = combineCurve(settings.mode, easing, previous);
        applied[item.id] = isLinear(next) ? null : { curve: next, keys: planKeys(next, settings.density) };
      });
      appliedLabel = label || tr('custom_curve', 'Custom');
      if (!Object.values(applied).some(Boolean)) appliedLabel = '';
      record('Apply', before);
      const properties = listNames(names.map(propName), (list, last) => tr('list_and', '[list] and [last]', { list, last }));
      const eased = label
        ? auto
          ? tr('auto_eased_with', 'Auto-eased [properties] with “[preset]”', { properties, preset: label })
          : tr('eased_with', 'Eased [properties] with “[preset]”', { properties, preset: label })
        : auto
          ? tr('auto_eased', 'Auto-eased [properties]', { properties })
          : tr('eased', 'Eased [properties]', { properties });
      toast(eased, 'success', {
        label: tr('undo', 'Undo'),
        run: undo,
      });
      renderTargets();
      monitor.replay();
    }

    function undo() {
      const step = undoStack.pop();
      if (!step) return;
      redoStack.push(step);
      restore(step.before);
      toast(step.label === 'Remove' ? tr('easing_back', "Put EaseFlow's easing back") : tr('easing_out', "Took EaseFlow's easing out"), 'success', {
        label: tr('redo', 'Redo'),
        run: redo,
      });
      renderTargets();
      monitor.replay();
    }

    function redo() {
      const step = redoStack.pop();
      if (!step) return;
      undoStack.push(step);
      restore(step.after);
      toast(step.label === 'Remove' ? tr('easing_out', "Took EaseFlow's easing out") : tr('easing_back', "Put EaseFlow's easing back"), 'success', {
        label: tr('undo', 'Undo'),
        run: undo,
      });
      renderTargets();
      monitor.replay();
    }

    const removeEasing = () => {
      const count = keyCount();
      if (!count) {
        toast(tr('nothing_to_remove', "There's no easing from EaseFlow here to remove."), 'warning');
        return;
      }
      const before = snapshot();
      applied = { position: null, scale: null, rotation: null };
      appliedLabel = '';
      record('Remove', before);
      const removed =
        count === 1
          ? tr('removed_one', "Removed EaseFlow's easing ([count] keyframe)", { count })
          : tr('removed_other', "Removed EaseFlow's easing ([count] keyframes)", { count });
      toast(removed, 'success', { label: tr('undo', 'Undo'), run: undo });
      renderTargets();
      monitor.replay();
    };

    $('apply').addEventListener('click', () => {
      // A curve typed but not entered yet is the one to apply.
      if (document.activeElement === field && fieldEdited() && !commitField()) return;
      apply();
    });

    // -------------------------------------------------------------------
    // The options menu (the ... in the header)

    let autoTimer = null;

    const toggleAutoEase = () => {
      settings.autoEase = !settings.autoEase;
      toast(settings.autoEase ? tr('auto_on', 'Auto Mode is on: easing applies when you create the second keyframe') : tr('auto_off', 'Auto Mode is off'));
      renderTargets();
      clearTimeout(autoTimer);
      if (!settings.autoEase) return;
      // Then what it does in Premiere: two new keyframes go in and the
      // pair is eased as soon as the second one lands - no click.
      autoTimer = setTimeout(() => {
        if (!settings.autoEase || !root.isConnected) return;
        applied = { position: null, scale: null, rotation: null };
        appliedLabel = '';
        monitor.newKeys();
        renderTargets();
        autoTimer = setTimeout(() => {
          if (settings.autoEase && root.isConnected) apply(editor.getCurve(), null, true);
        }, 500);
      }, 3600);
    };

    // "Undo Apply", "Redo Remove": the steps' own labels stay English inside.
    const stepLabel = (which, label) => {
      const english = `${which === 'undo' ? 'Undo' : 'Redo'} ${label}`;
      if (label === 'Apply') return tr(`${which}_apply`, english);
      if (label === 'Remove') return tr(`${which}_remove`, english);
      return english;
    };

    const optionsMenu = () => {
      const density = (value) => () => {
        settings.density = value;
      };
      const toggle = (key) => () => {
        settings[key] = !settings[key];
      };
      return [
        { heading: tr('menu_keyframes', 'Keyframes') },
        { label: tr('menu_smart', 'Smart'), hint: tr('menu_smart_hint', 'as few as the curve needs'), checked: settings.density === 'smart', run: density('smart') },
        { label: tr('menu_every_frame', 'Every Frame'), checked: settings.density === 'frame', run: density('frame') },
        '-',
        { heading: tr('menu_apply_auto', 'Apply Automatically') },
        { label: tr('menu_on_preset', 'When Clicking a Preset'), hint: tr('menu_on_preset_hint', 'eases right away'), checked: settings.applyOnClick, run: toggle('applyOnClick') },
        { label: tr('menu_on_edit', 'When Editing the Curve'), hint: tr('menu_on_edit_hint', 'eases as you let go'), checked: settings.applyEdits, run: toggle('applyEdits') },
        { label: tr('menu_auto_mode', 'Auto Mode'), hint: tr('menu_auto_mode_hint', 'new keyframes'), checked: settings.autoEase, run: toggleAutoEase },
        '-',
        { label: tr('menu_shortcuts', 'Keyboard Shortcuts…'), run: () => toast(tr('shortcuts_note', 'In Premiere Pro, any preset can have its own keyboard shortcut.')) },
        '-',
        { label: settings.scope === 'all' ? tr('menu_remove_all', 'Remove Easing from All Keyframes') : tr('menu_remove_playhead', 'Remove Easing at Playhead'), run: removeEasing },
        { label: undoStack.length ? stepLabel('undo', undoStack[undoStack.length - 1].label) : tr('undo', 'Undo'), disabled: !undoStack.length, run: undo },
        { label: redoStack.length ? stepLabel('redo', redoStack[redoStack.length - 1].label) : tr('redo', 'Redo'), disabled: !redoStack.length, run: redo },
        '-',
        { label: tr('menu_more_plugins', 'More Plugins'), run: () => window.location.assign('/collections/plugins') },
        { label: 'EaseFlow 2.0', disabled: true },
      ];
    };
    menuButton($('menu'), optionsMenu);
    $('auto').addEventListener('click', toggleAutoEase);
    $('still-chip').addEventListener('click', () => toast(tr('no_keys_toast', 'Opacity has no keyframes on this clip'), 'warning'));

    // Cmd/Ctrl+Z and Shift+Cmd/Ctrl+Z undo and redo while the panel has
    // focus, as in Premiere with EaseFlow's menu bar app.
    app.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && openMenuState) {
        closeMenu(true);
        return;
      }
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'z' && event.target !== field && !app.querySelector('.fpx-sheet-layer')) {
        event.preventDefault();
        if (event.shiftKey) redo();
        else undo();
      }
    });

    // -------------------------------------------------------------------
    // The Program Monitor: the clip moving with its keyframes

    const monitor = (() => {
      const screen = $('screen');
      const object = $('object');
      const label = $('screen-label');
      const timecode = $('tc');
      const playButton = $('play');
      const keys = $('keys');
      const playhead = $('keys-playhead');
      let playing = !calm();
      let visible = false;
      let frame = null;
      let started = performance.now();
      let pausedAt = 0;
      let shownKeys = '';

      const curveOf = (id) => (applied[id] ? applied[id].curve : LINEAR);

      // Where Position runs, as fractions of the screen's width: 0.2 to 0.8,
      // pulled in when the curve swings past its keyframes (a big spring,
      // Back) so the clip never leaves the frame.
      let travel = { from: 0.2, span: 0.6 };
      let travelKey = '';
      const fitTravel = () => {
        const curve = curveOf('position');
        const key = formatCurve(curve);
        if (key === travelKey) return;
        travelKey = key;
        const reach = reachOf([curve]);
        const low = reach.min;
        const high = reach.max;
        // the clip's centre stays this far in from either edge
        const edge = 0.2;
        const span = Math.min(0.6, (1 - 2 * edge) / (high - low));
        const from = Math.min(Math.max(0.5 - span / 2, edge - span * low), 1 - edge - span * high);
        travel = { from, span };
      };

      const draw = (t) => {
        const width = screen.clientWidth;
        const keysWidth = keys.clientWidth;
        // A property switched off below holds still on its last keyframe.
        const at = (id) => (settings.disabled.indexOf(id) !== -1 ? 1 : ease(curveOf(id), t));
        const position = at('position');
        const scale = at('scale');
        const rotation = at('rotation');
        const x = width * (travel.from + travel.span * position);
        object.style.transform = `translate3d(${x.toFixed(2)}px, 0, 0) rotate(${mix(-24, 0, rotation).toFixed(2)}deg) scale(${mix(0.55, 1, scale).toFixed(4)})`;
        const count = Math.round(t * FRAMES);
        const text = count >= FRAMES ? '00:00:01:00' : `00:00:00:${String(count).padStart(2, '0')}`;
        if (timecode.textContent !== text) timecode.textContent = text;
        playhead.style.transform = `translate3d(${(keysWidth * 0.88 * t).toFixed(2)}px, 0, 0)`;
      };

      const clipMoveMs = () => moveMsOf(PROPERTIES.map((item) => curveOf(item.id)));
      const clipCycleMs = () => CLIP_LEAD_MS + clipMoveMs() + CLIP_REST_MS;

      // Where the clip is `elapsed` ms into a loop, as a fraction of the move.
      const moveAt = (elapsed) => Math.min(1, Math.max(0, (elapsed - CLIP_LEAD_MS) / clipMoveMs()));

      const tick = (now) => {
        frame = null;
        const elapsed = (now - started) % clipCycleMs();
        draw(moveAt(elapsed));
        run();
      };

      const run = () => {
        if (frame === null && playing && visible && !document.hidden) frame = requestAnimationFrame(tick);
      };

      const stop = () => {
        if (frame !== null) {
          cancelAnimationFrame(frame);
          frame = null;
        }
      };

      const setPlaying = (value) => {
        if (value === playing) return;
        playing = value;
        playButton.setAttribute('aria-pressed', playing ? 'true' : 'false');
        if (playing) {
          // Carry on from where it was paused.
          started = performance.now() - (CLIP_LEAD_MS + pausedAt * clipMoveMs());
          run();
        } else {
          stop();
          pausedAt = moveAt((performance.now() - started) % clipCycleMs());
        }
      };

      playButton.setAttribute('aria-pressed', playing ? 'true' : 'false');
      playButton.addEventListener('click', () => setPlaying(!playing));

      // Drag along the keyframes to scrub.
      const scrubTo = (event) => {
        const box = keys.getBoundingClientRect();
        const t = Math.min(1, Math.max(0, (pointOf(event).x - box.left - box.width * 0.06) / (box.width * 0.88)));
        pausedAt = t;
        draw(t);
      };
      keys.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        event.preventDefault();
        setPlaying(false);
        try {
          keys.setPointerCapture(event.pointerId);
        } catch (error) {
          // moves still come while over the strip
        }
        keys.dataset.scrubbing = 'true';
        scrubTo(event);
      });
      keys.addEventListener('pointermove', (event) => {
        if (keys.dataset.scrubbing) scrubTo(event);
      });
      const endScrub = () => {
        delete keys.dataset.scrubbing;
      };
      keys.addEventListener('pointerup', endScrub);
      keys.addEventListener('pointercancel', endScrub);
      keys.style.cursor = 'ew-resize';
      keys.style.touchAction = 'pan-y';

      const renderKeys = () => {
        fitTravel();
        const shown = PROPERTIES.map((item) => applied[item.id]).find(Boolean);
        const frames = shown ? shown.keys : [];
        const key = frames.join(',');
        label.textContent = appliedLabel || 'Linear';
        label.toggleAttribute('data-eased', !!appliedLabel);
        if (key === shownKeys) return;
        shownKeys = key;
        keys.querySelectorAll('[data-fpx-generated]').forEach((node) => node.remove());
        frames.forEach((index, order) => {
          const node = element('span', `fpx__key-${pid} fpx__key--flow-${pid}`);
          node.dataset.fpxGenerated = 'true';
          node.style.left = `${(6 + (index / FRAMES) * 88).toFixed(3)}%`;
          node.style.animationDelay = `${Math.min(order * 18, 500)}ms`;
          keys.appendChild(node);
        });
      };

      const api = {
        render() {
          renderKeys();
          if (!playing || calm()) draw(playing ? 1 : pausedAt);
        },
        // From the start of the move, so the new easing shows right away.
        replay() {
          if (calm()) {
            draw(1);
            return;
          }
          started = performance.now() - CLIP_LEAD_MS * 0.6;
          if (!playing) setPlaying(true);
          run();
        },
        // Auto Mode's demo: two fresh keyframes on the clip.
        newKeys() {
          keys.querySelectorAll(`.fpx__key-${pid}:not([data-fpx-generated])`).forEach((node) => {
            node.animate([{ scale: 0.2, opacity: 0 }, { scale: 1, opacity: 1 }], { duration: 320, easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)' });
          });
          api.replay();
        },
        setVisible(value) {
          visible = value;
          if (visible) run();
          else stop();
        },
      };
      draw(playing ? 0 : 1);
      if (!playing) pausedAt = 1;
      return api;
    })();

    // -------------------------------------------------------------------
    // Start

    showCurve(editor.getCurve());
    markCurrentPreset(editor.getCurve());
    renderLibrary();
    renderMode();
    renderTargets();
    if (typeof ResizeObserver === 'function') new ResizeObserver(revealTab).observe(tabsHost);

    // Only animate while the panel is on screen (and the tab in front).
    let onScreen = false;
    const show = () => {
      const on = onScreen && !document.hidden;
      monitor.setVisible(on);
      editor.setVisible(on);
    };
    if (typeof IntersectionObserver === 'function') {
      new IntersectionObserver((entries) => {
        const was = onScreen;
        onScreen = entries[entries.length - 1].isIntersecting;
        show();
        if (onScreen && !was) editor.startMotion();
      }).observe(root);
    } else {
      onScreen = true;
      show();
      editor.startMotion();
    }
    document.addEventListener('visibilitychange', show);
  };

  const init = (scope) => {
    (scope || document).querySelectorAll('[data-flow-panel]').forEach(setup);
  };

  window.flowPanel = { init };
  init(document);
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();

